"use client";

import {
  Copy,
  CheckCircle,
  Calendar,
  User,
  FileText,
  Download,
  AlertCircle,
  Stethoscope,
  ClipboardList,
  Activity,
  Heart,
  Thermometer,
  Wind,
  Droplets,
  Eye,
  Mail,
  Shield,
  Pill,
  Siren,
  History,
  Users,
  TrendingUp,
  Brain,
  Zap,
  ThumbsUp,
  ThumbsDown,
  UserCircle,
  Clock,
  List,
  Users2,
  Edit3,
  FileStack,
  UserCheck,
  UsersRound,
  ChevronRight,
  ChevronDown
} from "lucide-react";
import { useRef } from "react";
import { useMemo } from "react";
import { useEffect } from "react";
import { useState, useCallback } from "react";
import EmailShareModal from "./EmailShareModal";
import MagicEditorButton from "./MagicEditorButton";
import NotepadEditorModal from "./NotepadEditorModal";
import NotepadEditorButton from "./NotepadEditorButton";
import FeedbackModal from "../ui/feedback-modal";
import { InlineEditableLine } from "../ui/inline-editable-line";
import MagicEditorWithWebSocket from "./MagicEditorWithWebSocket";
import SoapNotePronounBar from "./SoapNotePronounBar";
import { useAuth } from "../../context/AuthContext";
import { useTrial } from "../../context/TrialContext";
import { useFeedbackState } from "../../hooks/useFeedbackState";
import FeedbackHistoryModal from "../ui/feedback-history-modal";
import VersionIndicator from "../ui/version-indicator";
import { humanFeedbackService } from "../../apis/humanFeedbackService";
import { visitTypeApiService } from "../../apis/visitTypeService";
import { prechartingService } from "../../apis/prechartingService";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { adminService } from "../../apis/adminService";
import { AssignScribeModal } from "../scribe-tasks/AssignScribeModal";
import { AdminActionModal } from "../scribe-tasks/AdminActionModal";
import { SubmitReviewModal } from "../scribe-tasks/SubmitReviewModal";
import SoapIcd10ProblemListPanel, {
  Icd10AddCodeButton,
  Icd10CodeChips,
} from "./SoapIcd10SuggestionsPanel";
import Icd10CodeChangeModal from "./Icd10CodeChangeModal";
import Icd10AddCodeModal from "./Icd10AddCodeModal";
// Future: practice-partner highlight / trusted|external badges in SOAP notes
// import PracticePartnerDetailModal from "./PracticePartnerDetailModal";
// import { getPracticePartners } from "../../apis/userApiService";
// import {
//   annotatePracticePartnersInHtml,
//   flattenPracticePartnersFromProfile,
//   findPartnerByKey,
// } from "../../utils/practicePartnerAnnotate";
import {
  applyIcd10CodeToSoapSections,
  extractIcdCodesFromText,
  findDiagnosisLabelForCode,
  isIcdDiagnosisHeadingLine,
  replaceIcdCodeAndHeadingInText,
  replacePlanDiagnosisHeading,
  sortProblemsBySoapOrder,
  splitPlanFromFollowUp,
  splitTextByDiagnosisHeadings,
  collapseRedundantPlanHeadings,
  stripStandaloneAssessmentHeading,
  stripStandalonePlanHeading,
} from "../../utils/icd10Utils";

/** Set true to show the collapsible raw SOAP dump (dev/debug). Hidden from clinicians by default. */
const SHOW_COMPLETE_RAW_DOCUMENTATION = false;

export default function EnhancedSoapNoteDisplay({
  soapNote: initialSoapNote,
  patientContext,
  aiService,
  isAssignedSOAPNote = false,
  activeSession,
  typingAnimationNonce = 0,
  transcriptionPanel = null,
  hpiRegenerating = false,
  templateRegenerating = false,
  templateRegenLabel = "",
  isDemoMode = false,
  onDemoIcdChange = null,
  onDemoIcdAdd = null,
  onSoapNoteUpdate = null,
}) {
  const resolveDisplayName = (person, fallback = "Unknown") => {
    if (!person) return fallback;
    if (typeof person === "string" || typeof person === "number") {
      const value = String(person).trim();
      return value || fallback;
    }
    if (typeof person !== "object") return fallback;

    const profile = person.profile && typeof person.profile === "object" ? person.profile : null;
    const firstName =
      profile?.firstName ||
      profile?.firstname ||
      person.firstName ||
      person.firstname ||
      "";
    const lastName =
      profile?.lastName ||
      profile?.lastname ||
      person.lastName ||
      person.lastname ||
      "";
    const fullName = `${firstName} ${lastName}`.trim();
    if (fullName) return fullName;

    if (typeof profile?.fullName === "string" && profile.fullName.trim()) return profile.fullName.trim();
    if (typeof person.fullName === "string" && person.fullName.trim()) return person.fullName.trim();
    if (typeof person.name === "string" && person.name.trim()) return person.name.trim();
    if (typeof person.username === "string" && person.username.trim()) return person.username.trim();
    if (typeof profile?.email === "string" && profile.email.trim()) return profile.email.trim();
    if (typeof person.email === "string" && person.email.trim()) return person.email.trim();

    return fallback;
  };

  const normalizeUserId = (value) => {
    if (value === null || value === undefined) return "";
    return String(value).trim();
  };

  const resolveCreatorById = (creatorId) => {
    const id = normalizeUserId(creatorId);
    if (!id) return "";

    const matchedAssignable = (assignableUsers || []).find(
      (u) =>
        normalizeUserId(u?.userId) === id ||
        normalizeUserId(u?.id) === id ||
        normalizeUserId(u?.userid) === id
    );
    if (matchedAssignable) {
      return (
        resolveDisplayName(matchedAssignable, "") ||
        (typeof matchedAssignable?.name === "string" ? matchedAssignable.name : "") ||
        ""
      );
    }

    const currentUserId =
      normalizeUserId(currentUser?.userId) ||
      normalizeUserId(currentUser?.id) ||
      normalizeUserId(currentUser?.userid);
    if (currentUserId && currentUserId === id) {
      return resolveDisplayName(currentUser, "") || currentUser?.email || "";
    }

    return "";
  };

  const getCreatedByLabel = () => {
    const directName =
      resolveDisplayName(soapNote?.createdByUser, "") ||
      resolveDisplayName(soapNote?.createdBy, "") ||
      resolveDisplayName(soapNote?.creator, "") ||
      resolveDisplayName(soapNote?.user, "");
    if (directName && directName.toLowerCase() !== "unknown") return directName;

    const creatorIdCandidate =
      soapNote?.createdByUser?.userId ||
      soapNote?.createdByUser?.id ||
      soapNote?.createdByUser?.userid ||
      soapNote?.createdBy?.userId ||
      soapNote?.createdBy?.id ||
      soapNote?.createdBy?.userid ||
      soapNote?.creator?.userId ||
      soapNote?.creator?.id ||
      soapNote?.creator?.userid ||
      soapNote?.createdById ||
      soapNote?.creatorId ||
      soapNote?.userId;
    const mappedById = resolveCreatorById(creatorIdCandidate);
    if (mappedById) return mappedById;

    return (
      resolveDisplayName(soapNote?.createdByUser, "") ||
      resolveDisplayName(soapNote?.createdBy, "") ||
      resolveDisplayName(soapNote?.creator, "") ||
      resolveDisplayName(soapNote?.user, "Unknown")
    );
  };

  const [copied, setCopied] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [activeSection, setActiveSection] = useState("soap");
  const [copiedSection, setCopiedSection] = useState(null);
  const [soapNote, setSoapNote] = useState(initialSoapNote);
  const isDemo = isDemoMode || soapNote?.soapNoteId === "demo-soap-note";
  const { currentUser } = useAuth();
  const { isAiFeatureLocked, isProPlusLocked, getAccountLimitMessage } = useTrial();
  const [problemList, setProblemList] = useState([]);
  const [isLoadingProblemList, setIsLoadingProblemList] = useState(false);
  // Codes the user soft-deleted from the Problem List box. They are persisted
  // on the SOAP note (hiddenIcd10Codes) and never removed from its text.
  const hiddenIcdCodes = useMemo(() => {
    let raw = soapNote?.hiddenIcd10Codes;
    if (typeof raw === "string") {
      try {
        raw = JSON.parse(raw);
      } catch {
        raw = raw.split(/[,\s]+/);
      }
    }
    if (!Array.isArray(raw)) return [];
    return [
      ...new Set(
        raw.map((c) => String(c).trim().toUpperCase()).filter(Boolean),
      ),
    ];
  }, [soapNote?.hiddenIcd10Codes]);
  const [showAdminActionModal, setShowAdminActionModal] = useState({
    isOpen: false,
    action: null,
  });
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [loadingAssignableUsers, setLoadingAssignableUsers] = useState(false);
  const [assignmentStatus, setAssignmentStatus] = useState({
    isAssigning: false,
    success: false,
    error: null,
  });
  const [allSessions, setAllSessions] = useState([]);
  const [showSubmitReviewModal, setShowSubmitReviewModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [icdEditModal, setIcdEditModal] = useState({
    open: false,
    code: "",
    description: "",
  });
  const [icdAddModalOpen, setIcdAddModalOpen] = useState(false);
  // Future: practice-partner highlight / trusted|external badges
  // const [practicePartners, setPracticePartners] = useState([]);
  // const [partnerDetailModal, setPartnerDetailModal] = useState({
  //   open: false,
  //   kind: "external",
  //   partner: null,
  //   mentionName: "",
  // });
  // const partnerHoverTimerRef = useRef(null);
  const problemListRef = useRef(null);
  const navigate = useNavigate();

  const { isAdmin, isScriber, isScribeFullTime, isScribePartTime } =
    useMemo(() => {
      // Get role directly from currentUser.role (based on your console output)
      const role = (currentUser?.role || "").toLowerCase().trim();

      // Admin check
      const isAdminUser = role === "admin" || role === "administrator";

      // Scribe checks
      const isScriberUser = role.includes("scribe");
      const isScribeFullTime =
        role.includes("fulltime") || role.includes("full_time");
      const isScribePartTime =
        role.includes("parttime") || role.includes("part_time");

      return {
        isAdmin: isAdminUser,
        isScriber: isScriberUser && !isAdminUser,
        isScribeFullTime: isScriberUser && isScribeFullTime && !isAdminUser,
        isScribePartTime: isScriberUser && isScribePartTime && !isAdminUser,
      };
    }, [currentUser]);

  // Show back button for:
  // 1. Admin viewing assigned SOAP notes
  // 2. Scriber viewing assigned SOAP notes
  const shouldShowBackButton = (isAdmin || isScriber) && isAssignedSOAPNote;

  const getBackButtonConfig = () => {
    if (isAdmin) {
      return {
        label: "Back To Dashboard",
        path: "/scribe/assign-task",
      };
    } else if (isScriber) {
      return {
        label: "Back To My Tasks",
        path: "/scribe/dashboard",
      };
    }
    return null;
  };

  const backButtonConfig = getBackButtonConfig();

  // Determine if the SOAP note should be read-only once approved
  const isReadOnly = useMemo(() => {
    // Check if SOAP note is approved
    const isApproved = soapNote?.status === "approved";

    // For admins and scribers, make read-only when approved
    if ((isAdmin || isScriber) && isApproved) {
      return true;
    }

    return false;
  }, [soapNote?.status, isAdmin, isScriber]);

  useEffect(() => {
    const fetchLatestSOAPNote = async () => {
      if (!soapNote?.soapNoteId) return;
    // Skip demo soap note
    if (soapNote.soapNoteId === "demo-soap-note") return;

      try {
        const response = await aiService.getSOAPNote(soapNote.soapNoteId);

        let freshSoapNote = null;
        if (response.data?.body?.data?.soapNote) {
          freshSoapNote = response.data.body.data.soapNote;
        } else if (response.body?.data?.soapNote) {
          freshSoapNote = response.body.data.soapNote;
        } else if (response.data?.soapNote) {
          freshSoapNote = response.data.soapNote;
        } else if (response.soapNote) {
          freshSoapNote = response.soapNote;
        } else if (response.data?.data?.soapNote) {
          freshSoapNote = response.data.data.soapNote;
        }

        if (freshSoapNote) {
          setSoapNote(prev => ({
            ...prev,
            ...freshSoapNote,
            assignedTo: freshSoapNote.assignedTo ?? prev.assignedTo,
            status: freshSoapNote.status || prev.status,
          }));
        }
      } catch (error) {
        console.error("Failed to fetch latest SOAP note:", error);
      }
    };

    fetchLatestSOAPNote();
  }, [soapNote?.soapNoteId, aiService]);
  //fetch assignable users when admin opens the SOAP note
  useEffect(() => {
    const fetchAssignableUsers = async () => {
      if (isAdmin) {
        try {
          setLoadingAssignableUsers(true);
          const response = await adminService.getAssignableUsers();
          if (response.success) {
            setAssignableUsers(response.data?.users || []);
          }
        } catch (error) {
          console.error("Error fetching assignable users:", error);
        } finally {
          setLoadingAssignableUsers(false);
        }
      }
    };

    fetchAssignableUsers();
  }, [isAdmin]);

  // Add assignment handler functions
  const handleAssignClick = async () => {
    // 1. Ensure assignable users are loaded
    if (!assignableUsers || assignableUsers.length === 0) {
      setLoadingAssignableUsers(true);
      try {
        const response = await adminService.getAssignableUsers();
        if (response.success) {
          setAssignableUsers(response.data?.users || []);
        } else {
          toast.error("Failed to load assignable users");
          return;
        }
      } catch (error) {
        toast.error("Error loading assignable users");
        return;
      } finally {
        setLoadingAssignableUsers(false);
      }
    }

    // 2. Fetch the latest SOAP note to get fresh assignment status
    try {
      const freshNote = await aiService.getSOAPNote(soapNote.soapNoteId);
      if (freshNote.success && freshNote.data) {
        setSoapNote(prev => ({ ...prev, ...freshNote.data }));
      }
    } catch (error) {
      console.warn("Could not refresh SOAP note before assignment", error);
      // Continue anyway – the modal will use current data
    }

    setShowAssignModal(true);
  };

  const handleAssignSession = async (soapNoteId, assignedToName) => {
    try {
      setAssignmentStatus({ isAssigning: true, success: false, error: null });

      const user = assignableUsers.find((u) => u?.name === assignedToName);
      if (!user) {
        throw new Error("Selected scribe not found");
      }

      let response;

      if (soapNote?.assignedTo) {
        // Re-assign
        response = await adminService.reassignSOAPNote({
          soapNoteId,
          assignedTo: user.userId,
        });
      } else {
        // Assign
        response = await adminService.assignSOAPNote({
          soapNoteId,
          assignedTo: user.userId,
        });
      }

      if (response.success) {
        setAssignmentStatus({ isAssigning: false, success: true, error: null });
        toast.success(
          soapNote?.assignedTo
            ? `Successfully reassigned to ${assignedToName}`
            : `Successfully assigned to ${assignedToName}`,
        );

        // Update local state immediately
        setSoapNote((prev) => ({
          ...prev,
          assigned: true,
          assignedTo: user.userId,
          assignedToName: user.name,
          status: "assigned", // Explicitly set status to assigned
          assignedAt: new Date().toISOString(), // Add timestamp
        }));

        // Also update the session in allSessions if it exists there
        setAllSessions(prev => prev.map(session =>
          session.sessionId === soapNote.sessionId ? {
            ...session,
            status: "assigned",
            assignedTo: user.name,
            assignedToId: user.userId
          } : session
        ));

        setShowAssignModal(false);
      } else {
        throw new Error(response.message || "Assignment failed");
      }
    } catch (error) {
      setAssignmentStatus({
        isAssigning: false,
        success: false,
        error: error.message,
      });
      toast.error(`Assignment failed: ${error.message}`);
    }
  };

  const isSOAPNoteAssigned = useMemo(() => {
    // Use the dedicated 'assigned' column if available
    if (soapNote?.assigned !== undefined) {
      return soapNote.assigned === true;
    }
    // Fallback for older data (should not happen after backend fix)
    return !!(
      soapNote?.assignedTo ||
      soapNote?.assignedToName ||
      soapNote?.status === "assigned" ||
      soapNote?.status === "pending_review" ||
      soapNote?.status === "revision_requested" ||
      soapNote?.status === "approved"
    );
  }, [soapNote]);

  // Future: practice-partner highlight / trusted|external badges in SOAP notes
  // useEffect(() => {
  //   let cancelled = false;
  //   (async () => {
  //     try {
  //       const res = await getPracticePartners();
  //       const data = res?.data;
  //       if (cancelled || !data) return;
  //       setPracticePartners(flattenPracticePartnersFromProfile(data));
  //     } catch (err) {
  //       console.warn("Failed to load practice partners for chart annotations", err);
  //     }
  //   })();
  //   return () => {
  //     cancelled = true;
  //     if (partnerHoverTimerRef.current) {
  //       clearTimeout(partnerHoverTimerRef.current);
  //     }
  //   };
  // }, []);

  // const openPartnerDetailFromEl = useCallback(
  //   (el) => {
  //     if (!el) return;
  //     const kind = el.getAttribute("data-partner-kind") || "external";
  //     const key = el.getAttribute("data-partner-key") || "";
  //     const mentionName = el.getAttribute("data-partner-name") || "";
  //     const partner =
  //       kind === "known" ? findPartnerByKey(practicePartners, key || mentionName) : null;
  //     setPartnerDetailModal({
  //       open: true,
  //       kind,
  //       partner,
  //       mentionName,
  //     });
  //   },
  //   [practicePartners]
  // );

  // const handlePartnerMentionInteraction = useCallback(
  //   (e) => {
  //     const el = e.target?.closest?.(".practice-partner-mention");
  //     if (!el) return;
  //
  //     if (e.type === "click") {
  //       e.preventDefault();
  //       e.stopPropagation();
  //       if (partnerHoverTimerRef.current) {
  //         clearTimeout(partnerHoverTimerRef.current);
  //         partnerHoverTimerRef.current = null;
  //       }
  //       openPartnerDetailFromEl(el);
  //       return;
  //     }
  //
  //     if (e.type === "mouseover") {
  //       if (partnerHoverTimerRef.current) {
  //         clearTimeout(partnerHoverTimerRef.current);
  //       }
  //       partnerHoverTimerRef.current = setTimeout(() => {
  //         openPartnerDetailFromEl(el);
  //       }, 450);
  //     }
  //   },
  //   [openPartnerDetailFromEl]
  // );

  // const handlePartnerMentionMouseOut = useCallback((e) => {
  //   const el = e.target?.closest?.(".practice-partner-mention");
  //   if (!el) return;
  //   if (partnerHoverTimerRef.current) {
  //     clearTimeout(partnerHoverTimerRef.current);
  //     partnerHoverTimerRef.current = null;
  //   }
  // }, []);

  // Add this helper function to convert soap note to session format for modal
  const convertSoapNoteToSession = (soapNote) => {
    if (!soapNote) return null;
    const isAssigned = soapNote.assigned === true;
    const assignedUser = isAssigned ? assignableUsers.find(
      user => user.userId === soapNote.assignedTo
    ) : null;

    return {
      id: soapNote.soapNoteId,
      sessionId: soapNote.sessionId,
      patientName: patientContext?.name || "Unknown Patient",
      status: soapNote.status || "unknown",
      assignedTo: isAssigned ? (assignedUser?.name || soapNote.assignedToName || "-") : "-",
      assignedToId: isAssigned ? soapNote.assignedTo : null,
      assignedDate: isAssigned && soapNote.assignedAt ? new Date(soapNote.assignedAt).toLocaleDateString() : null,
      dueDate: soapNote.dueDate,
      lastEdited: soapNote.updatedAt ? new Date(soapNote.updatedAt).toLocaleDateString() : "N/A",
      rawData: soapNote,
    };
  };

  const handleSubmitForReview = async (notes) => {
    setActionLoading(true);
    try {
      const response = await aiService.submitSOAPNoteForReview(
        soapNote.soapNoteId,
        { notes, isResubmission: soapNote?.status === "revision_requested" },
      );

      if (response.success) {
        toast.success("SOAP note submitted for review!");

        // Update local state immediately
        setSoapNote((prev) => ({
          ...prev,
          status: "pending_review",
          submittedAt: new Date().toISOString(),
        }));

        setShowSubmitReviewModal(false);

        // Also update section content state if needed
        if (loadFeedbackState) {
          await loadFeedbackState();
        }
      } else {
        throw new Error(response.message || "Submission failed");
      }
    } catch (error) {
      toast.error(`Failed to submit for review: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAdminAction = async (actionData) => {
    setActionLoading(true);
    try {
      let response;
      if (showAdminActionModal.action === "approve") {
        response = await aiService.approveSOAPNote(soapNote.soapNoteId, {
          comments: actionData.comments,
        });
      } else {
        response = await aiService.requestSOAPNoteRevisions(
          soapNote.soapNoteId,
          {
            comments: actionData.comments,
            deadline: actionData.deadline,
          },
        );
      }

      if (response.success) {
        toast.success(
          showAdminActionModal.action === "approve"
            ? "SOAP note approved!"
            : "Revisions requested!",
        );

        // Update local state
        const updatedData = {
          status:
            showAdminActionModal.action === "approve"
              ? "approved"
              : "revision_requested",
        };

        if (showAdminActionModal.action === "approve") {
          updatedData.approvalComments = actionData.comments;
          updatedData.approvedAt = new Date().toISOString();
        } else {
          updatedData.revisionComments = actionData.comments;
          updatedData.revisionDeadline = actionData.deadline;
        }

        setSoapNote((prev) => ({
          ...prev,
          ...updatedData,
        }));

        // Close modal
        setShowAdminActionModal({ isOpen: false, action: null });

        // Reload feedback state to update versions
        if (loadFeedbackState) {
          await loadFeedbackState();
        }
      } else {
        throw new Error(response.message || "Action failed");
      }
    } catch (error) {
      toast.error(
        `Failed to ${showAdminActionModal.action === "approve" ? "approve" : "request revisions"}: ${error.message}`,
      );
    } finally {
      setActionLoading(false);
    }
  };

  const extractProblemsFromSoapNote = useCallback((soapNote) => {
    const problems = [];

    if (!soapNote) return problems;
    const assessmentContent =
      soapNote.assessment || soapNote.structuredSections?.assessment || "";

    const canonicalPattern =
      /\*\*\s*([^*]+?)\s*\(\s*([A-Z]\d{2}(?:\.\d{1,3})?)\s*\)\s*\*\*/gi;
    let match;
    while ((match = canonicalPattern.exec(assessmentContent)) !== null) {
      const code = match[2].toUpperCase();
      const condition = match[1].trim().replace(/:\s*$/, "");
      if (!problems.some((p) => p.code === code)) {
        problems.push({
          code,
          description: condition,
          source: "soap_note",
          id: `soap-${code}-canonical`,
        });
      }
    }
    const diagnosisPattern =
      /(\d+)\.\s*\*\*([^*]+)\*\*[^(]*\(([A-Z]\d{2}(?:\.\d{1,3})?)\)/gi;
    while ((match = diagnosisPattern.exec(assessmentContent)) !== null) {
      const code = match[3].toUpperCase();
      const condition = match[2].trim();
      if (!problems.some((p) => p.code === code)) {
        problems.push({
          code: code,
          description: condition,
          source: "soap_note",
          id: `soap-${code}-${match[1]}`,
        });
      }
    }
    const bulletPattern =
      /^\s*-\s*\*\*([^*]+?)\s*\(\s*([A-Z]\d{2}(?:\.\d{1,3})?)\s*\)\s*:?\s*\*\*/gim;
    while ((match = bulletPattern.exec(assessmentContent)) !== null) {
      const code = match[2].toUpperCase();
      const condition = match[1].trim().replace(/:\s*$/, "");
      if (!problems.some((p) => p.code === code)) {
        problems.push({
          code,
          description: condition,
          source: "soap_note",
          id: `soap-${code}-bullet`,
        });
      }
    }
    const icdPattern =
      /(?:ICD[- ]?10[- ]?CM?[- ]?:?|\(|\[)([A-Z]\d{2}(?:\.\d{1,3})?)(?:\)|\])?/gi;
    const foundCodes = new Set();
    let codeMatch;

    while ((codeMatch = icdPattern.exec(assessmentContent)) !== null) {
      const code = codeMatch[1].toUpperCase();
      if (code.match(/^[A-Z]\d{2}(?:\.\d{1,3})?$/)) {
        foundCodes.add(code);
      }
    }

    foundCodes.forEach((code) => {
      const exists = problems.some((p) => p.code === code);
      if (!exists) {
        const codeIndex = assessmentContent.indexOf(code);
        if (codeIndex !== -1) {
          const context = assessmentContent.substring(
            Math.max(0, codeIndex - 150),
            codeIndex + 50,
          );
          const beforeCode = context.substring(0, context.indexOf(code));
          const descriptionMatch = beforeCode.match(
            /([^(\n]+?)(?:\s*\([A-Z]\d)/,
          );
          const description = descriptionMatch
            ? descriptionMatch[1].trim().replace(/\*\*/g, "")
            : "";

          problems.push({
            code: code,
            description: description || "",
            source: "soap_note",
            id: `soap-${code}-${Date.now()}`,
          });
        }
      }
    });

    if (soapNote.icd10Codes) {
      let codes = [];
      if (Array.isArray(soapNote.icd10Codes)) {
        codes = soapNote.icd10Codes;
      } else if (typeof soapNote.icd10Codes === "string") {
        codes = soapNote.icd10Codes.split(/[,\s]+/).filter(Boolean);
      }

      codes.forEach((code) => {
        const cleanCode = code.trim().toUpperCase();
        if (cleanCode.match(/^[A-Z]\d{2}(?:\.\d{1,3})?$/)) {
          const exists = problems.some((p) => p.code === cleanCode);
          if (!exists) {
            problems.push({
              code: cleanCode,
              description: "",
              source: "soap_note",
              id: `soap-${cleanCode}-direct`,
            });
          }
        }
      });
    }

    return problems;
  }, []);

  const {
    getSectionFeedbackState,
    updateFeedbackState,
    getParagraphFeedbackState,
    loadFeedbackState,
    getCurrentSectionVersion,
    hasSectionBeenEdited,
  } = useFeedbackState(soapNote?.soapNoteId);

  const [showFeedbackHistory, setShowFeedbackHistory] = useState(false);
  const [selectedSectionForHistory, setSelectedSectionForHistory] =
    useState(null);

  const normalizeCopyText = (text = "") =>
    text
      .replace(/\r\n/g, "\n")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();

  const normalizeContentForVersionComparison = (text = "") => {
    const withoutFormattingTags = String(text)
      .replace(/<br\s*\/?>/gi, " ")
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;|&#160;/gi, " ");

    return withoutFormattingTags.replace(/\s+/g, " ").trim();
  };

  const normalizeVersionContent = (text = "") =>
    String(text)
      .replace(/\r\n/g, "\n")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/([^\n])\n(?=\*\*[^*\n]+\*\*\s*:)/g, "$1\n\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();

  const handleCopyClick = useCallback(async (section, text) => {
    try {
      await navigator.clipboard.writeText(normalizeCopyText(text));
      setCopiedSection(section);
      setTimeout(() => {
        setCopiedSection(null);
      }, 300);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  }, []);

  const handleShowFeedbackHistory = useCallback((section) => {
    setSelectedSectionForHistory(section);
    setShowFeedbackHistory(true);
  }, []);

  const handleCloseFeedbackHistory = useCallback(() => {
    setShowFeedbackHistory(false);
    setSelectedSectionForHistory(null);
  }, []);

  useEffect(() => {
    setSoapNote(initialSoapNote);
  }, [
    initialSoapNote,
    initialSoapNote?.soapNoteId,
    initialSoapNote?.version,
    initialSoapNote?.templateId,
    initialSoapNote?.metadata?.templateInstanceId,
    initialSoapNote?.subjective,
    initialSoapNote?.objective,
    initialSoapNote?.assessment,
    initialSoapNote?.plan,
  ]);

  // When the SAME note's revision changes (e.g. after an HPI depth regen), the
  // section-version badges live in useFeedbackState which only loads on
  // soapNoteId. Refetch them so the version badge updates live (no reload).
  const lastVersionKeyRef = useRef(null);
  useEffect(() => {
    const id = initialSoapNote?.soapNoteId;
    if (!id) return;
    const version = initialSoapNote?.version ?? 1;
    const prev = lastVersionKeyRef.current;
    lastVersionKeyRef.current = { id, version };
    if (!prev) return; // first run: useFeedbackState already loads on mount
    if (prev.id !== id) return; // note switched: hook reloads via soapNoteId
    if (prev.version !== version) {
      loadFeedbackState();
    }
  }, [initialSoapNote?.soapNoteId, initialSoapNote?.version, loadFeedbackState]);

  useEffect(() => {
    if (initialSoapNote?.soapNoteId) {
      setCopiedSection(null);
      // setEditingSection(null);
      // setFeedbackSubmitted({
      //   subjective: null,
      //   objective: null,
      //   assessmentAndPlan: null,
      // });
    }
  }, [initialSoapNote?.soapNoteId, initialSoapNote?.version]);

  // Fetch problem list from precharting data and combine with SOAP note problems
  useEffect(() => {
    const fetchAndCombineProblemList = async () => {
      const codeMap = new Map(); // To deduplicate by ICD code

      //skip demo session
    if (soapNote?.sessionId === "demo-session") {
      setProblemList([]);
      setIsLoadingProblemList(false);
      return;
    }

      // 1. Fetch from precharting data (authoritative DB-backed list)
      if (soapNote?.sessionId) {
        try {
          setIsLoadingProblemList(true);
          const response = await prechartingService.getPrechartingData(
            soapNote.sessionId,
          );
          if (response.success && response.data?.problemList) {
            const prechartingProblems = Array.isArray(response.data.problemList)
              ? response.data.problemList
              : [];

            prechartingProblems.forEach((problem) => {
              if (problem.code) {
                const code = problem.code.toUpperCase();
                codeMap.set(code, {
                  code: code,
                  description: problem.description || "",
                  source: "precharting",
                  // If backend starts sending priority/weight, preserve them
                  priority: problem.priority ?? null,
                  weight: problem.weight ?? null,
                  id: problem.id || `precharting-${code}`,
                });
              }
            });
          }
        } catch (error) {
          console.error("Error fetching precharting problem list:", error);
        }
      }

      const soapProblems = extractProblemsFromSoapNote(soapNote);
      soapProblems.forEach((problem) => {
        const code = problem.code.toUpperCase();
        // Only add if not already in precharting (precharting takes priority)
        if (!codeMap.has(code)) {
          codeMap.set(code, problem);
        }
      });

      // 3. Fetch canonical descriptions / priority from ICD DB,
      //    and fall back to GPT-generated descriptions when not in DB
      const problemsArray = Array.from(codeMap.values());
      const problemsWithDescriptions = await Promise.all(
        problemsArray.map(async (problem) => {
          // For precharting problems we assume descriptions already match DB
          if (problem.source === "precharting") {
            return {
              ...problem,
              descriptionSource: "db",
            };
          }

          // For SOAP-derived problems, always try DB lookup so headings/descriptions
          // match the canonical values stored in our ICD table.
          try {
            const lookupResponse = await prechartingService.lookupICD10Code(
              problem.code,
            );

            if (lookupResponse.success && lookupResponse.data) {
              const dbData = lookupResponse.data;
              const metadata = dbData.metadata || {};

              return {
                ...problem,
                description:
                  dbData.description ||
                  dbData.fullDescription ||
                  problem.description ||
                  "",
                // Optional priority/weight coming from DB (if present)
                priority:
                  metadata.priority ?? metadata.weightPriority ?? problem.priority ?? null,
                weight:
                  metadata.weight ?? metadata.score ?? problem.weight ?? null,
                descriptionSource: "db",
              };
            }
          } catch (error) {
            console.error(
              `Error fetching description for code ${problem.code}:`,
              error,
            );
          }

          // If not found in DB, keep the transcription/GPT-derived description
          // and explicitly mark it as GPT-generated so charts can show that.
          return {
            ...problem,
            description: problem.description || "",
            descriptionSource: "gpt",
          };
        }),
      );

      // 4. Sort by database-driven priority/weight when available
      const sortedProblems = [...problemsWithDescriptions].sort((a, b) => {
        // Lower numeric priority comes first
        if (a.priority != null && b.priority != null) {
          if (a.priority !== b.priority) return a.priority - b.priority;
        } else if (a.priority != null) {
          return -1;
        } else if (b.priority != null) {
          return 1;
        }

        // Higher weight comes first when priorities are equal/missing
        if (a.weight != null && b.weight != null) {
          if (a.weight !== b.weight) return b.weight - a.weight;
        } else if (a.weight != null) {
          return -1;
        } else if (b.weight != null) {
          return 1;
        }

        // Fallback: alphabetical by code
        return a.code.localeCompare(b.code);
      });

      setProblemList(sortedProblems);
      setIsLoadingProblemList(false);
    };

    if (soapNote) {
      fetchAndCombineProblemList();
    } else {
      setProblemList([]);
      setIsLoadingProblemList(false);
    }
  }, [
    soapNote?.sessionId,
    soapNote?.assessment,
    soapNote?.icd10Codes,
    soapNote?.soapNoteId,
    extractProblemsFromSoapNote,
  ]);

  const [showMagicEditor, setShowMagicEditor] = useState(false);

  useEffect(() => {
    if (isAiFeatureLocked || isProPlusLocked) {
      setShowMagicEditor(false);
    }
  }, [isAiFeatureLocked, isProPlusLocked]);

  const [showEmailShare, setShowEmailShare] = useState(false);
  const [showNotepadEditor, setShowNotepadEditor] = useState(false);
  const [soapSectionLoading, setSoapSectionLoading] = useState({
    subjective: false,
    objective: false,
    assessment: false,
    plan: false,
  });

  const SoapSectionSkeleton = () => (
    <div className="space-y-3 animate-pulse">
      <div className="space-y-2">
        <div className="h-4 bg-gray-300 rounded w-full"></div>
        <div className="h-4 bg-gray-300 rounded w-5/6"></div>
        <div className="h-4 bg-gray-300 rounded w-4/5"></div>
        <div className="h-4 bg-gray-300 rounded w-3/4"></div>
        <div className="h-4 bg-gray-300 rounded w-2/3"></div>
      </div>
      <div className="space-y-2">
        <div className="h-4 bg-gray-300 rounded w-full"></div>
        <div className="h-4 bg-gray-300 rounded w-5/6"></div>
        <div className="h-4 bg-gray-300 rounded w-4/5"></div>
      </div>
    </div>
  );
  const [feedbackModal, setFeedbackModal] = useState({
    isOpen: false,
    section: "",
    type: "",
    content: "",
    contentType: "",
  });
  const [feedbackSubmitted, setFeedbackSubmitted] = useState({
    subjective: null,
    objective: null,
    assessmentAndPlan: null,
  });
  const splitBullets = (text) =>
    (text || "")
      .replace(/\r\n/g, "\n")
      .split(
        /\n(?=\s*[•\-]\s+|\*\*[^*\n]+\([A-Z][A-Z0-9.]*\)\*\*|\*\*(?:Follow-Up Instructions|Signature):\*\*)/,
      )
      .map((s) => s.trimEnd())
      .filter((s) => s.trim().length > 0);

  const splitPlanBlocks = (text) => {
    const normalized = (text || "").replace(/\r\n/g, "\n").trim();
    if (!normalized) return [];

    const lines = normalized.split("\n");
    const blocks = [];
    let current = [];

    const isPlanSectionHeading = (line) =>
      /^\s*[-*•]?\s*(?:\*\*)?\s*(Follow[-\s]*Up(?:[-\s]*Instructions?)?|Signature)\s*:?\s*(?:\*\*)?\s*(?:.*)?$/i.test(
        line || "",
      );
    const isBlockStart = (line) =>
      isPlanSectionHeading(line) || isIcdDiagnosisHeadingLine(line);

    for (const line of lines) {
      if (isBlockStart(line) && current.length > 0) {
        blocks.push(current.join("\n").trim());
        current = [line];
      } else {
        current.push(line);
      }
    }

    if (current.length > 0) {
      blocks.push(current.join("\n").trim());
    }

    return blocks.filter(Boolean);
  };

  const splitByKnownHeadings = (text) => {
    const normalized = (text || "").replace(/\r\n/g, "\n").trim();
    if (!normalized) return [];
    const headingPattern =
      /^\s*[-*•]?\s*(?:\*\*)?\s*(CC|Chief Complaint(?:\s*\(CC\))?|HPI|ROS|PMH|Surgical History|Hospitalization History|Medications|Medication Intolerance|Allergies|Social History|Family History|Preventive Medicine|Vitals|Physical Exam|Labs|Imaging\/Studies|Procedures|Follow-Up-Instructions|Follow[-\s]*Up(?:[-\s]*Instructions?)?|Signature)\s*:?\s*(?:\*\*)?/gim;

    const matches = [...normalized.matchAll(headingPattern)];
    if (matches.length === 0) return [];

    const blocks = [];
    for (let i = 0; i < matches.length; i += 1) {
      const start = matches[i].index ?? 0;
      const end = i + 1 < matches.length ? (matches[i + 1].index ?? normalized.length) : normalized.length;
      const chunk = normalized.slice(start, end).trim();
      if (chunk) blocks.push(chunk);
    }
    return blocks;
  };

  const splitParagraphs = (text) =>
    text
      .split(/\n\s*\n/)
      .map((s) => s.trim())
      .filter(Boolean);

  const splitSectionIntoBlocks = (section, text) => {
    let normalized = (text || "")
      .replace(/\r\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
    if (!normalized) return [];

    if (section === "assessment") {
      normalized = collapseRedundantPlanHeadings(
        stripStandaloneAssessmentHeading(normalized),
      );
      if (!normalized) return [];
      const diagnosisBlocks = splitTextByDiagnosisHeadings(normalized);
      if (diagnosisBlocks.length > 0) return diagnosisBlocks;
      return splitParagraphs(normalized);
    }

    if (section === "plan") {
      const planBlocks = splitPlanBlocks(normalized);
      if (planBlocks.length > 0) return planBlocks;
      const headingBlocks = splitByKnownHeadings(normalized);
      return headingBlocks.length > 1 ? headingBlocks : splitParagraphs(normalized);
    }

    if (section === "subjective" || section === "objective") {
      const headingBlocks = splitByKnownHeadings(normalized);
      if (headingBlocks.length > 1) return headingBlocks;
    }

    const bullets = splitBullets(normalized);
    return bullets.length > 1 ? bullets : splitParagraphs(normalized);
  };

  const parseSoapContent = (soapNote) => {
    const structured = soapNote?.structuredSections;
    // Prefer top-level fields (ICD / inline edits update these) and fall back
    // to structuredSections so a stale nested copy cannot wipe a live update.
    return {
      subjective: soapNote?.subjective || structured?.subjective || "",
      objective: soapNote?.objective || structured?.objective || "",
      assessment: soapNote?.assessment || structured?.assessment || "",
      plan: soapNote?.plan || structured?.plan || "",
    };
  };

  const extractAdvancedData = (soapNote) => {
    const data = {
      metadata: {
        generatedAt:
          soapNote.createdAt ||
          soapNote.metadata?.generatedAt ||
          new Date().toISOString(),
        model: soapNote.aiModel || soapNote.metadata?.model || "Unknown",
        tokensUsed: soapNote.metadata?.tokensUsed || 0,
        completionPercentage: soapNote.validation?.completionPercentage || 0,
        isValid: soapNote.validation?.isValid || false,
        processingTime:
          soapNote.processingTime || soapNote.metadata?.processingTimeMs || 0,
        hpiDepthLevel: soapNote.metadata?.hpiDepthLevel || 3,
      },
      patientInfo: extractPatientInfo(soapNote),
      vitals: extractVitalSigns(soapNote),
      diagnostics: extractDiagnostics(soapNote),
      medications: extractMedications(soapNote),
      procedures: extractProcedures(soapNote),
      followUp: extractFollowUp(soapNote),
      riskAssessment: extractRiskAssessment(soapNote),
      clinicalInsights: extractClinicalInsights(soapNote),
    };

    return data;
  };

  const extractPatientInfo = (soapNote) => {
    const content =
      soapNote.subjective || soapNote.structuredSections?.subjective || "";

    return {
      age: content.match(/(\d+)-year-old/)?.[1] || null,
      gender: content.match(/(\d+)-year-old\s+(male|female)/i)?.[2] || null,
      chiefComplaint: extractChiefComplaint(content),
      duration: content.match(/Duration:\s*([^\n\)]+)/)?.[1] || null,
      severity: content.match(/Severity:\s*([^\n\)]+)/)?.[1] || null,
      allergies: soapNote.allergies || extractAllergies(content),
      medicalHistory:
        soapNote.pastMedicalHistory || extractMedicalHistory(content),
      socialHistory: soapNote.socialHistory || extractSocialHistory(content),
    };
  };

  const extractChiefComplaint = (content) => {
    // Look for various CC patterns
    const patterns = [
      /\*\*CC:\*\*\s*[""""]([^"""]*)[""""]/,
      /Chief Complaint:?\s*[""""]([^"""]*)[""""]/,
      /\*\*CC:\*\*\s*([^\n]+)/,
      /Chief Complaint:?\s*([^\n]+)/,
    ];

    for (const pattern of patterns) {
      const match = content.match(pattern);
      if (match && match[1]) {
        return match[1].trim().replace(/^["']|["']$/g, "");
      }
    }

    return null;
  };

  const extractVitalSigns = (soapNote) => {
    const content =
      soapNote.objective || soapNote.structuredSections?.objective || "";

    return {
      bloodPressure:
        content.match(/(?:BP|Blood Pressure):\s*(\d+\/\d+)/i)?.[1] || null,
      heartRate:
        content.match(/(?:HR|Heart Rate|Pulse):\s*(\d+)/i)?.[1] || null,
      temperature:
        content.match(/(?:Temp|Temperature):\s*([\d.]+).*?F/i)?.[1] || null,
      respiratoryRate:
        content.match(/(?:RR|Respiratory Rate):\s*(\d+)/i)?.[1] || null,
      oxygenSaturation:
        content.match(/(?:O2 Sat|SpO2):\s*(\d+)%/i)?.[1] || null,
      weight: content.match(/Weight:\s*([\d.]+)\s*(?:lbs?|kg)/i)?.[1] || null,
      height:
        content.match(/Height:\s*([\d'\"]+|[\d.]+\s*(?:cm|ft))/i)?.[1] || null,
    };
  };

  const extractDiagnostics = (soapNote) => {
    const content =
      soapNote.assessment || soapNote.structuredSections?.assessment || "";
    const diagnoses = [];

    // Extract numbered diagnoses with ICD codes
    const diagnosisPattern = /(\d+)\.\s*\*\*([^*]+)\*\*[^(]*\(([^)]+)\)/g;
    let match;
    while ((match = diagnosisPattern.exec(content)) !== null) {
      diagnoses.push({
        priority: parseInt(match[1]),
        condition: match[2].trim(),
        code: match[3].trim(),
        details: extractDiagnosisDetails(content, match[2]),
      });
    }

    return {
      primaryDiagnoses: diagnoses.filter((d) => d.priority <= 2),
      secondaryDiagnoses: diagnoses.filter((d) => d.priority > 2),
      totalDiagnoses: diagnoses.length,
      riskFactors: extractRiskFactors(content),
    };
  };

  const extractMedications = (soapNote) => {
    const content = soapNote.plan || soapNote.structuredSections?.plan || "";
    const medications = [];

    // Look for medication patterns
    const medPatterns = [
      /Medications:\s*([^*]+)(?=\*\*|\n\n)/g,
      /prescribed[^.]*([A-Z][a-z]+(?:\s+\d+mg|\s+\d+mcg)?)/gi,
    ];

    medPatterns.forEach((pattern) => {
      let matches;
      if (pattern.global) {
        while ((matches = pattern.exec(content)) !== null) {
          if (
            matches[1] &&
            !matches[1].includes("None") &&
            !matches[1].includes("Not documented")
          ) {
            medications.push(matches[1].trim());
          }
        }
        pattern.lastIndex = 0;
      } else {
        const match = content.match(pattern);
        if (
          match &&
          match[1] &&
          !match[1].includes("None") &&
          !match[1].includes("Not documented")
        ) {
          medications.push(match[1].trim());
        }
      }
    });

    return medications;
  };

  const extractProcedures = (soapNote) => {
    const content =
      soapNote.plan ||
      soapNote.objective ||
      soapNote.structuredSections?.plan ||
      soapNote.structuredSections?.objective ||
      "";
    const procedures = [];

    const procedureKeywords = [
      "imaging",
      "x-ray",
      "CT",
      "MRI",
      "ultrasound",
      "blood work",
      "lab",
      "biopsy",
      "surgery",
    ];

    procedureKeywords.forEach((keyword) => {
      const regex = new RegExp(`[^.]*${keyword}[^.]*`, "gi");
      const matches = content.match(regex);
      if (matches) {
        procedures.push(...matches.map((m) => m.trim()));
      }
    });

    return procedures;
  };

  const extractFollowUp = (soapNote) => {
    const content = soapNote.plan || soapNote.structuredSections?.plan || "";

    return {
      timeframe:
        content.match(
          /follow[- ]?up[^.]*?(\d+\s*(?:days?|weeks?|months?))/i,
        )?.[1] || null,
      instructions:
        content.match(/Follow[- ]?up:\s*([^*\n]+)/i)?.[1]?.trim() || null,
      returnConditions: extractReturnConditions(content),
      appointments: extractAppointments(content),
    };
  };

  const extractRiskAssessment = (soapNote) => {
    const content = soapNote.rawContent || "";

    return {
      riskLevel: determineRiskLevel(content),
      complications: extractComplications(content),
      preventionMeasures: extractPreventionMeasures(content),
      monitoringParams: extractMonitoringParams(content),
    };
  };

  const extractClinicalInsights = (soapNote) => {
    return {
      complexity:
        soapNote.metadata?.tokensUsed > 2000
          ? "High"
          : soapNote.metadata?.tokensUsed > 1000
            ? "Medium"
            : "Low",
      completeness: soapNote.validation?.completionPercentage || 0,
      confidence: soapNote.validation?.isValid ? "High" : "Medium",
      keyFindings: extractKeyFindings(soapNote),
      clinicalPearls: extractClinicalPearls(soapNote),
    };
  };

  // Helper functions for extraction
  const extractAllergies = (content) => {
    const allergyMatch = content.match(/Allergies:\s*([^*\n]+)/i);
    if (
      allergyMatch &&
      !allergyMatch[1].includes("Not documented") &&
      !allergyMatch[1].includes("None")
    ) {
      return allergyMatch[1]
        .split(",")
        .map((a) => a.trim())
        .filter((a) => a);
    }
    return [];
  };

  const extractMedicalHistory = (content) => {
    const pmhMatch = content.match(
      /(?:PMH|Past Medical History):\s*([^*]+?)(?=\*\*|---)/s,
    );
    if (pmhMatch && !pmhMatch[1].includes("Not documented")) {
      return pmhMatch[1]
        .split(/[-•]/)
        .map((h) => h.trim())
        .filter((h) => h && h.length > 3);
    }
    return [];
  };

  const extractSocialHistory = (content) => {
    const social = {};
    const patterns = [
      { key: "tobacco", regex: /tobacco:\s*([^,\n]+)/i },
      { key: "alcohol", regex: /alcohol:\s*([^,\n]+)/i },
      { key: "occupation", regex: /occupation:\s*([^,\n]+)/i },
      { key: "living", regex: /lives?\s+(independently|alone|with)/i },
    ];

    patterns.forEach(({ key, regex }) => {
      const match = content.match(regex);
      if (match && !match[1].includes("Not documented")) {
        social[key] = match[1].trim();
      }
    });

    return social;
  };

  const extractDiagnosisDetails = (content, condition) => {
    const conditionSection =
      content.split(condition)[1]?.split(/\d+\./)[0] || "";

    return {
      prognosis:
        conditionSection.match(/prognosis[^.]*([^.]+)/i)?.[1]?.trim() || null,
      treatment:
        conditionSection.match(/treatment[^.]*([^.]+)/i)?.[1]?.trim() || null,
      monitoring:
        conditionSection.match(/monitor[^.]*([^.]+)/i)?.[1]?.trim() || null,
    };
  };

  const extractRiskFactors = (content) => {
    const riskKeywords = ["risk", "concern", "warning", "caution", "monitor"];
    const risks = [];

    riskKeywords.forEach((keyword) => {
      const regex = new RegExp(`[^.]*${keyword}[^.]*`, "gi");
      const matches = content.match(regex);
      if (matches) {
        risks.push(...matches.map((m) => m.trim()).slice(0, 2));
      }
    });

    return risks.slice(0, 5);
  };

  const extractReturnConditions = (content) => {
    const returnMatch = content.match(/return.*?if[^.]*([^.]+)/i);
    return returnMatch
      ? returnMatch[1]
        .trim()
        .split(/[,;]/)
        .map((c) => c.trim())
      : [];
  };

  const extractAppointments = (content) => {
    const appointments = [];
    const apptMatches = content.matchAll(
      /(?:follow[- ]?up|appointment)[^.]*?(\d+\s*(?:days?|weeks?|months?))/gi,
    );
    for (const match of apptMatches) {
      appointments.push(match[1]);
    }
    return appointments;
  };

  const extractComplications = (content) => {
    const complications = [];
    const compKeywords = ["complication", "adverse", "side effect", "reaction"];

    compKeywords.forEach((keyword) => {
      const matches = content.match(new RegExp(`[^.]*${keyword}[^.]*`, "gi"));
      if (matches)
        complications.push(...matches.map((m) => m.trim()).slice(0, 1));
    });

    return complications;
  };

  const extractPreventionMeasures = (content) => {
    const prevention = [];
    const preventKeywords = ["prevent", "avoid", "reduce risk", "precaution"];

    preventKeywords.forEach((keyword) => {
      const matches = content.match(new RegExp(`[^.]*${keyword}[^.]*`, "gi"));
      if (matches) prevention.push(...matches.map((m) => m.trim()).slice(0, 1));
    });

    return prevention;
  };

  const extractMonitoringParams = (content) => {
    const monitoring = [];
    const monitorKeywords = ["monitor", "watch for", "observe", "check"];

    monitorKeywords.forEach((keyword) => {
      const matches = content.match(new RegExp(`[^.]*${keyword}[^.]*`, "gi"));
      if (matches) monitoring.push(...matches.map((m) => m.trim()).slice(0, 1));
    });

    return monitoring;
  };

  const determineRiskLevel = (content) => {
    const highRiskTerms = [
      "emergency",
      "urgent",
      "critical",
      "severe",
      "immediate",
    ];
    const mediumRiskTerms = [
      "moderate",
      "concern",
      "monitor closely",
      "caution",
    ];

    if (highRiskTerms.some((term) => content.toLowerCase().includes(term)))
      return "High";
    if (mediumRiskTerms.some((term) => content.toLowerCase().includes(term)))
      return "Medium";
    return "Low";
  };

  const extractKeyFindings = (soapNote) => {
    const findings = [];
    const content =
      soapNote.objective || soapNote.structuredSections?.objective || "";

    const findingPatterns = [
      /([A-Z][^.]*(?:positive|negative|abnormal|normal|elevated|decreased)[^.]*)/g,
      /([A-Z][^.]*(?:findings?|results?|noted)[^.]*)/g,
    ];

    findingPatterns.forEach((pattern) => {
      let match;
      while ((match = pattern.exec(content)) !== null) {
        if (match[1] && match[1].length > 10) {
          findings.push(match[1].trim());
        }
      }
      pattern.lastIndex = 0;
    });

    return findings.slice(0, 3);
  };

  const extractClinicalPearls = (soapNote) => {
    const pearls = [];
    const content = soapNote.rawContent || "";

    const pearlPatterns = [
      /(?:note|important|remember|pearl):\s*([^.]+)/gi,
      /\*\*([^*]+)\*\*.*?(?:consider|important|note)/gi,
    ];

    pearlPatterns.forEach((pattern) => {
      let match;
      while ((match = pattern.exec(content)) !== null) {
        if (match[1] && match[1].length > 15) {
          pearls.push(match[1].trim());
        }
      }
      pattern.lastIndex = 0;
    });

    return pearls.slice(0, 2);
  };

  //   const parsedSections = soapNote
  //     ? parseSoapContent(soapNote)
  //     : { subjective: "", objective: "", assessment: "", plan: "" };

  //   // Always call useState at top level
  //  const [sectionContent, setSectionContent] = useState({
  //   subjective: parsedSections.subjective || "",
  //   objective: parsedSections.objective || "",
  //   assessment: parsedSections.assessment || "",
  //   plan: parsedSections.plan || "",
  // });

  // const [sectionContent, setSectionContent] = useState({
  //   subjective: "",
  //   objective: "",
  //   assessment: "",
  //   plan: "",
  // });
  //   const advancedData = useMemo(() => extractAdvancedData(soapNote), [soapNote]);
  //   const parsedSections = useMemo(() => parseSoapContent(soapNote),[soapNote])
  // useEffect(() => {
  //   if (soapNote) {
  //     const parsed = parseSoapContent(soapNote);
  //     setSectionContent({
  //       subjective: parsed.subjective || "",
  //       objective: parsed.objective || "",
  //       assessment: parsed.assessment || "",
  //       plan: parsed.plan || "",
  //     });
  //   }
  // }, [soapNote]);

  //using use ref for avoiding rerender

  const subjectiveRef = useRef("");
  const objectiveRef = useRef("");
  const assessmentRef = useRef("");
  const planRef = useRef("");
  const animatedSoapRenderKeyRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingNonceRef = useRef(typingAnimationNonce);
  const sectionBodyRefs = useRef({});

  const [sectionContent, setSectionContent] = useState({
    subjective: "",
    objective: "",
    assessment: "",
    plan: "",
  });

  const keepTypingSectionInView = (sectionKey) => {
    const sectionId =
      sectionKey === "assessment" || sectionKey === "plan"
        ? "assessmentAndPlan"
        : sectionKey;
    const sectionBody = sectionBodyRefs.current[sectionId];
    if (!sectionBody) return;

    // Keep the active section scrolling as new lines are typed.
    sectionBody.scrollTop = sectionBody.scrollHeight;

    // If the section reaches the viewport edge, nudge page scroll down smoothly.
    if (typeof window !== "undefined") {
      const rect = sectionBody.getBoundingClientRect();
      const viewportBottom = window.innerHeight - 90;
      if (rect.bottom > viewportBottom) {
        window.scrollBy({
          top: Math.min(220, rect.bottom - viewportBottom),
          behavior: "smooth",
        });
      }
    }
  };

  const advancedData = useMemo(
    () => extractAdvancedData(soapNote),
    [
      soapNote?.soapNoteId,
      soapNote?.version,
      soapNote?.templateId,
      soapNote?.metadata?.templateInstanceId,
      soapNote?.subjective,
      soapNote?.objective,
      soapNote?.assessment,
      soapNote?.plan,
    ],
  );
  const parsedSections = useMemo(
    () => parseSoapContent(soapNote),
    [
      soapNote?.soapNoteId,
      soapNote?.version,
      soapNote?.templateId,
      soapNote?.metadata?.templateInstanceId,
      soapNote?.subjective,
      soapNote?.objective,
      soapNote?.assessment,
      soapNote?.plan,
      soapNote?.structuredSections?.subjective,
      soapNote?.structuredSections?.objective,
      soapNote?.structuredSections?.assessment,
      soapNote?.structuredSections?.plan,
    ],
  );

  // Update section content when soapNote changes
  useEffect(() => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    if (soapNote) {
      const parsed = parseSoapContent(soapNote);

      subjectiveRef.current = parsed.subjective || "";
      objectiveRef.current = parsed.objective || "";
      assessmentRef.current = parsed.assessment || "";
      planRef.current = parsed.plan || "";

      const soapId = soapNote?.soapNoteId || "";
      const soapVersion = soapNote?.version ?? "0";
      const renderKey = `${soapId}-${soapVersion}`;

      const newSectionContent = {
        subjective: parsed.subjective || "",
        objective: parsed.objective || "",
        assessment: parsed.assessment || "",
        plan: parsed.plan || "",
      };
      const hasAnyContent = Object.values(newSectionContent).some(
        (value) => typeof value === "string" && value.trim().length > 0
      );
      const shouldAnimateForThisRender = typingAnimationNonce !== lastTypingNonceRef.current;
      const shouldAnimate =
        shouldAnimateForThisRender &&
        !!soapId &&
        hasAnyContent &&
        animatedSoapRenderKeyRef.current !== renderKey;

      if (!shouldAnimate) {
        setSectionContent(newSectionContent);
        return;
      }

      lastTypingNonceRef.current = typingAnimationNonce;
      animatedSoapRenderKeyRef.current = renderKey;
      setSectionContent({
        subjective: "",
        objective: "",
        assessment: "",
        plan: "",
      });

      const sectionOrder = ["subjective", "objective", "assessment", "plan"];
      const charsPerTick = 2;
      const tickMs = 26;
      const sectionPauseMs = 220;

      const typeSection = (sectionIndex, charIndex) => {
        const sectionKey = sectionOrder[sectionIndex];
        if (!sectionKey) {
          typingTimeoutRef.current = null;
          return;
        }

        const targetText = newSectionContent[sectionKey] || "";
        const nextCharIndex = Math.min(targetText.length, charIndex + charsPerTick);

        setSectionContent((prev) => ({
          ...prev,
          [sectionKey]: targetText.slice(0, nextCharIndex),
        }));
        if (typeof window !== "undefined") {
          window.requestAnimationFrame(() => keepTypingSectionInView(sectionKey));
        }

        if (nextCharIndex >= targetText.length) {
          typingTimeoutRef.current = setTimeout(
            () => typeSection(sectionIndex + 1, 0),
            sectionPauseMs
          );
          return;
        }

        typingTimeoutRef.current = setTimeout(
          () => typeSection(sectionIndex, nextCharIndex),
          tickMs
        );
      };

      typingTimeoutRef.current = setTimeout(() => typeSection(0, 0), 120);
    }
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
    };
  }, [
    soapNote?.soapNoteId,
    soapNote?.version,
    soapNote?.templateId,
    soapNote?.metadata?.templateInstanceId,
    soapNote?.subjective,
    soapNote?.objective,
    soapNote?.assessment,
    soapNote?.plan,
    soapNote?.structuredSections?.subjective,
    soapNote?.structuredSections?.objective,
    soapNote?.structuredSections?.assessment,
    soapNote?.structuredSections?.plan,
    typingAnimationNonce,
  ]);

  // Expand SOAP sections while template regen is in progress so Magic Editor-style skeletons are visible.
  useEffect(() => {
    if (!templateRegenerating) return;
    setCollapsedSections((prev) => ({
      ...prev,
      subjective: false,
      objective: false,
      assessmentAndPlan: false,
    }));
  }, [templateRegenerating]);

  // const [editedFlags, setEditedFlags] = useState({
  //   subjective: false,
  //   objective: false,
  //   assessmentPlan: false, // combined for assessment & plan
  // });

  if (!soapNote) return null;

  // Enhanced data extraction from the new response format

  // Parse the SOAP note data
  // Parse the SOAP content safely before hooks

  // const advancedData = extractAdvancedData(soapNote);

  // Enhanced content formatter that cleans markdown and structures text
  const formatContent = (content) => {
    if (!content) return "No content available";

    // Remove markdown formatting
    let formatted = content
      .replace(/\*\*(.*?)\*\*/g, "$1")
      .replace(/- \*\*(.*?)\*\*/g, "• $1")
      .replace(/^\s*-\s*/gm, "• ")
      .replace(/\n\s*\n/g, "\n\n")
      .trim();

    // Split into paragraphs and clean each one
    const paragraphs = formatted
      .split("\n\n")
      .map((para) => {
        return para
          .split("\n")
          .map((line) => line.trim())
          .filter((line) => line.length > 0)
          .join("\n");
      })
      .filter((para) => para.length > 0);

    return paragraphs.join("\n\n");
  };

  const handleCopy = () => {
    // Create formatted SOAP note content
    const formattedContent = `SOAP NOTE
Generated: ${new Date(advancedData.metadata.generatedAt).toLocaleDateString()}
Patient: ${patientContext?.name || "Unknown"}

SUBJECTIVE:
${formatContent(parsedSections.subjective)}

OBJECTIVE:
${formatContent(parsedSections.objective)}

ASSESSMENT:
${formatContent(parsedSections.assessment)}

PLAN:
${formatContent(parsedSections.plan)}`;

    navigator.clipboard
      .writeText(formattedContent)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((err) => console.error("Copy failed:", err));
  };

  const handleVersionRestored = async () => {
    if (!soapNote?.soapNoteId || soapNote.soapNoteId === "demo-soap-note") return;

    const response = await aiService.getSOAPNote(soapNote.soapNoteId);
    const freshSoapNote =
      response.data?.body?.data?.soapNote ||
      response.body?.data?.soapNote ||
      response.data?.soapNote ||
      response.soapNote ||
      response.data?.data?.soapNote;

    if (!freshSoapNote) {
      throw new Error("Restored SOAP note was not returned by the server");
    }

    const updatedSoapNote = {
      ...soapNote,
      ...freshSoapNote,
      structuredSections: {
        ...(soapNote?.structuredSections || {}),
        ...(freshSoapNote.structuredSections || {}),
      },
    };

    setSoapNote(updatedSoapNote);
    onSoapNoteUpdate?.(updatedSoapNote);
  };

  const saveInlineEdit = async (section, newText, paragraphIndex = null) => {
    try {
      let contentToSave;

      if (paragraphIndex !== null) {
        const currentContent = sectionContent[section] || "";
        const paragraphs = splitSectionIntoBlocks(section, currentContent);

        paragraphs[paragraphIndex] = newText;

        const updatedContent = paragraphs.join("\n\n");
        contentToSave = {
          [section]: updatedContent,
        };
      } else {
        contentToSave = {
          [section]: newText,
        };
      }

      const res = await aiService.updateSOAPNote(soapNote.soapNoteId, {
        ...contentToSave,
        editedFlags: {
          subjective: section === "subjective",
          objective: section === "objective",
          assessmentPlan:
            section === "assessment" ||
            section === "plan" ||
            section === "assessmentPlan",
        },
      });

      if (res?.data?.statusCode === true) {
        setSectionContent((prev) => ({
          ...prev,
          ...contentToSave,
        }));

        if (section === "subjective") {
          subjectiveRef.current = contentToSave[section];
        } else if (section === "objective") {
          objectiveRef.current = contentToSave[section];
        } else if (section === "assessment") {
          assessmentRef.current = contentToSave[section];
        } else if (section === "plan") {
          planRef.current = contentToSave[section];
        }

        // Reload feedback state to reflect any version changes
        if (loadFeedbackState) {
          await loadFeedbackState();
        }
      } else {
        throw new Error(res?.data?.message || "Failed to save section");
      }
    } catch (err) {
      console.error("Failed to save inline edit:", err);
      throw err;
    }
  };

  const savePlanMergedBlock = async (part, blockIndex, newText) => {
    const { clinicalPlan, followUp } = splitPlanFromFollowUp(
      sectionContent.plan || "",
    );
    let merged;
    if (part === "clinical") {
      const blocks = splitSectionIntoBlocks("plan", clinicalPlan);
      blocks[blockIndex] = newText;
      merged = [blocks.join("\n\n"), followUp].filter(Boolean).join("\n\n");
    } else {
      const blocks = splitSectionIntoBlocks("plan", followUp);
      blocks[blockIndex] = newText;
      merged = [clinicalPlan, blocks.join("\n\n")].filter(Boolean).join("\n\n");
    }
    await saveInlineEdit("plan", merged, null);
  };

  const onUpdateSoapNote = async (
    soapNoteId,
    section,
    newText,
    paragraphIndex,
  ) => {
    try {
      let contentToSave;

      if (paragraphIndex !== null && paragraphIndex !== undefined) {
        const currentContent = sectionContent[section] || "";
        const paragraphs = splitSectionIntoBlocks(section, currentContent);

        paragraphs[paragraphIndex] = newText;
        const updatedContent = paragraphs.join("\n\n");
        contentToSave = {
          [section]: updatedContent,
        };
      } else {
        contentToSave = {
          [section]: newText,
        };
      }

      const res = await aiService.updateSOAPNote(soapNoteId, {
        ...contentToSave,
        editedFlags: {
          subjective: section === "subjective" ? true : false,
          objective: section === "objective" ? true : false,
          assessmentPlan:
            section === "assessment" || section === "plan" ? true : false,
        },
      });

      if (res?.data?.statusCode === true) {
        setSectionContent((prev) => ({
          ...prev,
          ...contentToSave,
        }));

        if (section === "subjective") {
          subjectiveRef.current = contentToSave[section];
        } else if (section === "objective") {
          objectiveRef.current = contentToSave[section];
        } else if (section === "assessment") {
          assessmentRef.current = contentToSave[section];
        } else if (section === "plan") {
          planRef.current = contentToSave[section];
        }

        if (loadFeedbackState) {
          await loadFeedbackState();
        }
      } else {
        throw new Error(res?.data?.message || "Failed to update SOAP note");
      }
    } catch (err) {
      console.error("Failed to update SOAP note:", err);
      throw err;
    }
  };

  // Soft delete / restore for the Problem List box. Only the hidden-codes list
  // is saved; assessment/plan text and icd10Codes are left untouched.
  const persistHiddenIcdCodes = async (nextHidden) => {
    if (isReadOnly && !isDemo) return false;
    const previousHidden = soapNote?.hiddenIcd10Codes ?? null;
    // The parent keeps its own copy of the note and re-applies it to this
    // component whenever it changes (see the setSoapNote(initialSoapNote)
    // effect). Keep it in sync, otherwise a stale copy without the hidden
    // codes overwrites them again, e.g. after a reload.
    const syncParent = () =>
      onSoapNoteUpdate?.({ ...soapNote, hiddenIcd10Codes: nextHidden });
    setSoapNote((prev) => ({ ...prev, hiddenIcd10Codes: nextHidden }));
    if (isDemo || !soapNote?.soapNoteId) {
      syncParent();
      return true;
    }
    try {
      const res = await aiService.updateSOAPNote(soapNote.soapNoteId, {
        hiddenIcd10Codes: nextHidden,
      });
      if (res?.data?.statusCode === true) {
        syncParent();
        return true;
      }
      throw new Error(res?.data?.message || "Failed to save your choice");
    } catch (err) {
      console.error("Failed to save hidden ICD-10 codes:", err);
      setSoapNote((prev) => ({ ...prev, hiddenIcd10Codes: previousHidden }));
      toast.error(err.message || "Failed to save your choice");
      return false;
    }
  };

  const handleHideIcd10Code = async (code) => {
    const normalized = String(code || "").trim().toUpperCase();
    if (!normalized || hiddenIcdCodes.includes(normalized)) return false;
    const ok = await persistHiddenIcdCodes([...hiddenIcdCodes, normalized]);
    if (ok) {
      toast.success(`${normalized} removed from the list (kept in SOAP note)`);
    }
    return ok;
  };

  const handleRestoreIcd10Code = async (code) => {
    const normalized = String(code || "").trim().toUpperCase();
    if (!normalized || !hiddenIcdCodes.includes(normalized)) return false;
    return persistHiddenIcdCodes(hiddenIcdCodes.filter((c) => c !== normalized));
  };

  const handleAddIcd10Code = async (code, description) => {
    //Demo
    if (isDemo) {
  if (onDemoIcdAdd) {
    return await onDemoIcdAdd(code, description);
  } else {
    toast.error("Demo mode: ICD codes cannot be modified.");
    return false;
  }
}
    if (isReadOnly || !soapNote?.soapNoteId) return false;
    try {
      const normalizedCode = code.toUpperCase();
      const exists = problemList.some(
        (p) => p.code.toUpperCase() === normalizedCode,
      );
      if (exists) {
        // Re-adding a soft-deleted code just brings it back into the list.
        if (hiddenIcdCodes.includes(normalizedCode)) {
          return await handleRestoreIcd10Code(normalizedCode);
        }
        return false;
      }

      let icd10Codes = [];
      if (Array.isArray(soapNote.icd10Codes)) {
        icd10Codes = [...soapNote.icd10Codes];
      } else if (typeof soapNote.icd10Codes === "string" && soapNote.icd10Codes) {
        icd10Codes = soapNote.icd10Codes.split(/[,\s]+/).filter(Boolean);
      }
      if (!icd10Codes.includes(normalizedCode)) {
        icd10Codes.push(normalizedCode);
      }

      const { assessment: updatedAssessment, plan: updatedPlan, changed } =
        applyIcd10CodeToSoapSections(
          sectionContent.assessment || "",
          sectionContent.plan || "",
          description,
          normalizedCode,
        );

      const payload = { icd10Codes };
      if (changed) {
        payload.assessment = updatedAssessment;
        payload.plan = updatedPlan;
      }
      payload.editedFlags = { assessmentPlan: true };

      const res = await aiService.updateSOAPNote(soapNote.soapNoteId, payload);

      if (res?.data?.statusCode === true) {
        const newProblem = {
          code: normalizedCode,
          description: description || "",
          source: "manual",
          id: `manual-${normalizedCode}-${Date.now()}`,
        };
        setProblemList((prev) =>
          [...prev, newProblem].sort((a, b) => a.code.localeCompare(b.code)),
        );
        setSoapNote((prev) => ({
          ...prev,
          icd10Codes,
          assessment: payload.assessment ?? prev.assessment,
          plan: payload.plan ?? prev.plan,
          structuredSections: prev.structuredSections
            ? {
                ...prev.structuredSections,
                ...(payload.assessment != null
                  ? { assessment: payload.assessment }
                  : {}),
                ...(payload.plan != null ? { plan: payload.plan } : {}),
              }
            : prev.structuredSections,
        }));
        if (payload.assessment || payload.plan) {
          setSectionContent((prev) => ({
            ...prev,
            assessment: payload.assessment ?? prev.assessment,
            plan: payload.plan ?? prev.plan,
          }));
          if (payload.assessment) {
            assessmentRef.current = payload.assessment;
          }
          if (payload.plan) {
            planRef.current = payload.plan;
          }
        }
        if (loadFeedbackState) await loadFeedbackState();
        return true;
      }
      throw new Error(res?.data?.message || "Failed to save ICD-10 code");
    } catch (err) {
      console.error("Failed to add ICD-10 code:", err);
      toast.error(err.message || "Failed to add ICD-10 code");
      return false;
    }
  };

  const openIcdCodeEditModal = (code) => {
    const normalized = String(code || "").trim().toUpperCase();
    if (!normalized) return;
    const fromList = problemList.find(
      (p) => p.code.toUpperCase() === normalized,
    );
    const fromAssessment = findDiagnosisLabelForCode(
      sectionContent.assessment || "",
      normalized,
    );
    setIcdEditModal({
      open: true,
      code: normalized,
      description: fromList?.description || fromAssessment || "",
    });
  };

  const handleReplaceIcd10Code = async (oldCode, newCode, newDescription) => {
    //Demo
    if (isDemo) {
  if (onDemoIcdChange) {
    return await onDemoIcdChange(oldCode, newCode, newDescription);
  } else {
    toast.error("Demo mode: ICD codes cannot be modified.");
    return false;
  }
}
    if (isReadOnly || !soapNote?.soapNoteId) return false;
    const oldNorm = oldCode.toUpperCase();
    const newNorm = newCode.toUpperCase();
    const desc = (newDescription || "").trim();

    try {
      const assessmentText = sectionContent.assessment || "";
      const planText = sectionContent.plan || "";
      const oldHeading =
        findDiagnosisLabelForCode(assessmentText, oldNorm) ||
        findDiagnosisLabelForCode(planText, oldNorm) ||
        icdEditModal.description ||
        "";

      const updatedAssessment = replaceIcdCodeAndHeadingInText(
        assessmentText,
        oldNorm,
        newNorm,
        desc,
        oldHeading,
      );
      const updatedPlan = replacePlanDiagnosisHeading(
        replaceIcdCodeAndHeadingInText(
          planText,
          oldNorm,
          newNorm,
          desc,
          oldHeading,
        ),
        oldNorm,
        desc,
        newNorm,
        oldHeading,
      );

      if (
        oldNorm === newNorm &&
        updatedAssessment === assessmentText &&
        updatedPlan === planText
      ) {
        return true;
      }

      let icd10Codes = [];
      if (Array.isArray(soapNote.icd10Codes)) {
        icd10Codes = soapNote.icd10Codes.map((c) =>
          String(c).toUpperCase() === oldNorm ? newNorm : String(c).toUpperCase(),
        );
      } else if (typeof soapNote.icd10Codes === "string" && soapNote.icd10Codes) {
        icd10Codes = soapNote.icd10Codes
          .split(/[,\s]+/)
          .filter(Boolean)
          .map((c) => (c.toUpperCase() === oldNorm ? newNorm : c.toUpperCase()));
      }
      if (!icd10Codes.includes(newNorm)) icd10Codes.push(newNorm);
      icd10Codes = [...new Set(icd10Codes.filter((c) => c !== oldNorm))];

      const payload = {
        icd10Codes,
        editedFlags: { assessmentPlan: true },
      };
      // A replaced code that was soft-deleted stays soft-deleted under its new code.
      if (oldNorm !== newNorm && hiddenIcdCodes.includes(oldNorm)) {
        payload.hiddenIcd10Codes = [
          ...new Set(
            hiddenIcdCodes.map((c) => (c === oldNorm ? newNorm : c)),
          ),
        ];
      }
      if (updatedAssessment !== sectionContent.assessment) {
        payload.assessment = updatedAssessment;
      }
      if (updatedPlan !== sectionContent.plan) {
        payload.plan = updatedPlan;
      }

      const res = await aiService.updateSOAPNote(soapNote.soapNoteId, payload);

      if (res?.data?.statusCode === true) {
        setProblemList((prev) =>
          prev.map((p) =>
            p.code.toUpperCase() === oldNorm
              ? {
                ...p,
                code: newNorm,
                description: newDescription || p.description,
                source: p.source === "precharting" ? "precharting" : "manual",
              }
              : p,
          ),
        );
        setSoapNote((prev) => ({
          ...prev,
          icd10Codes,
          ...(payload.hiddenIcd10Codes
            ? { hiddenIcd10Codes: payload.hiddenIcd10Codes }
            : {}),
          assessment: payload.assessment ?? prev.assessment,
          plan: payload.plan ?? prev.plan,
          // Keep structuredSections in sync — parseSoapContent prefers it,
          // so skipping this reverts the chart until a full refresh.
          structuredSections: prev.structuredSections
            ? {
                ...prev.structuredSections,
                ...(payload.assessment != null
                  ? { assessment: payload.assessment }
                  : {}),
                ...(payload.plan != null ? { plan: payload.plan } : {}),
              }
            : prev.structuredSections,
        }));
        setSectionContent((prev) => ({
          ...prev,
          assessment: payload.assessment ?? prev.assessment,
          plan: payload.plan ?? prev.plan,
        }));
        if (payload.assessment) assessmentRef.current = payload.assessment;
        if (payload.plan) planRef.current = payload.plan;
        if (loadFeedbackState) await loadFeedbackState();
        const headingNote = desc ? ` — heading updated` : "";
        toast.success(
          oldNorm === newNorm
            ? `Updated diagnosis heading for ${newNorm}${headingNote}`
            : `Updated ${oldNorm} → ${newNorm}${headingNote}`,
        );
        return true;
      }
      throw new Error(res?.data?.message || "Failed to update ICD-10 code");
    } catch (err) {
      console.error("Failed to replace ICD-10 code:", err);
      toast.error(err.message || "Failed to change ICD-10 code");
      return false;
    }
  };

  const generateAdvancedPDF = async () => {
    setIsGeneratingPdf(true);
    try {
      const printWindow = window.open("", "_blank");

      const pdfContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>SOAP Note - ${advancedData.patientInfo.age || "Unknown"} ${advancedData.patientInfo.gender || ""
        }</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.6; color: #2d3748; }
            .container { max-width: 1200px; margin: 0 auto; padding: 20px; }
            .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 30px; border-radius: 12px; margin-bottom: 30px; }
            .header h1 { font-size: 2.5em; margin-bottom: 10px; }
            .metadata { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px; margin-top: 20px; }
            .metadata-item { background: rgba(255,255,255,0.1); padding: 15px; border-radius: 8px; }
            .section { background: white; border-radius: 12px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); margin-bottom: 25px; overflow: hidden; }
            .section-header { background: linear-gradient(135deg, #4299e1 0%, #3182ce 100%); color: white; padding: 20px; font-size: 1.3em; font-weight: 600; }
            .section-content { padding: 25px; white-space: pre-line; }
            .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
            .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; }
            .vital-card { background: #f7fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 15px; text-align: center; }
            .vital-value { font-size: 1.8em; font-weight: bold; color: #2d3748; }
            .vital-label { color: #718096; font-size: 0.9em; margin-top: 5px; }
            .diagnosis-item { background: #fff5f5; border-left: 4px solid #f56565; padding: 15px; margin-bottom: 10px; border-radius: 0 8px 8px 0; }
            .high-priority { border-left-color: #e53e3e; }
            .medium-priority { border-left-color: #ed8936; }
            .low-priority { border-left-color: #38a169; }
            .tag { display: inline-block; background: #edf2f7; color: #4a5568; padding: 4px 12px; border-radius: 20px; font-size: 0.85em; margin: 2px; }
            .risk-high { background: #fed7d7; color: #c53030; }
            .risk-medium { background: #feebc8; color: #dd6b20; }
            .risk-low { background: #c6f6d5; color: #2f855a; }
            @media print { body { font-size: 11pt; } .section { page-break-inside: avoid; } }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>SOAP Documentation</h1>
              <div class="metadata">
                <div class="metadata-item">
                  <strong>Generated:</strong> ${new Date(
          advancedData.metadata.generatedAt,
        ).toLocaleDateString()}
                </div>
                <div class="metadata-item">
                  <strong>Model:</strong> ${advancedData.metadata.model}
                </div>
                <div class="metadata-item">
                  <strong>Completeness:</strong> ${advancedData.metadata.completionPercentage
        }%
                </div>
                <div class="metadata-item">
                  <strong>Tokens:</strong> ${advancedData.metadata.tokensUsed}
                </div>
              </div>
            </div>

            <div class="section">
              <div class="section-header">Patient Overview</div>
              <div class="section-content">
                <div class="grid-2">
                  <div>
                  ${patientContext?.name &&
        `<p><strong>Patient Name:</strong> ${patientContext.name}</p>`
        }
                    ${advancedData.patientInfo.age
          ? `<p><strong>Age:</strong> ${advancedData.patientInfo.age} years</p>`
          : ""
        }
                    ${advancedData.patientInfo.gender
          ? `<p><strong>Gender:</strong> ${advancedData.patientInfo.gender}</p>`
          : ""
        }
                    ${advancedData.patientInfo.chiefComplaint
          ? `<p><strong>Chief Complaint:</strong> ${advancedData.patientInfo.chiefComplaint}</p>`
          : ""
        }
                    ${advancedData.patientInfo.duration
          ? `<p><strong>Duration:</strong> ${advancedData.patientInfo.duration}</p>`
          : ""
        }
                  </div>
                  <div>
                    <p><strong>Risk Level:</strong> <span class="tag risk-${advancedData.riskAssessment.riskLevel.toLowerCase()}">${advancedData.riskAssessment.riskLevel
        }</span></p>
                    <p><strong>Complexity:</strong> ${advancedData.clinicalInsights.complexity
        }</p>
                    <p><strong>Confidence:</strong> ${advancedData.clinicalInsights.confidence
        }</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="section">
              <div class="section-header">SOAP Documentation</div>
              <div class="section-content">
                <div class="grid-2">
                  <div>
                    <h4 style="color: #4299e1; margin-bottom: 15px;">Subjective</h4>
                    <p>${formatContent(parsedSections.subjective)}</p>
                  </div>
                  <div>
                    <h4 style="color: #4299e1; margin-bottom: 15px;">Objective</h4>
                    <p>${formatContent(parsedSections.objective)}</p>
                  </div>
                </div>
                <div class="grid-2" style="margin-top: 25px;">
                  <div>
                    <h4 style="color: #4299e1; margin-bottom: 15px;">Assessment</h4>
                    <p>${formatContent(parsedSections.assessment)}</p>
                  </div>
                  <div>
                    <h4 style="color: #4299e1; margin-bottom: 15px;">Plan</h4>
                    <p>${formatContent(parsedSections.plan)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </body>
        </html>
      `;

      printWindow.document.write(pdfContent);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 1000);
    } catch (error) {
      console.error("PDF generation failed:", error);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const VitalSignCard = ({
    icon: Icon,
    label,
    value,
    unit,
    status = "normal",
  }) => {
    if (!value) return null;

    const statusColors = {
      normal: "border-green-200 bg-green-50",
      warning: "border-yellow-200 bg-yellow-50",
      critical: "border-red-200 bg-red-50",
    };

    return (
      <div
        className={`p-4 rounded-xl border-2 ${statusColors[status]} transition-all hover:scale-105`}
      >
        <div className="flex items-center justify-between mb-2">
          <Icon className="w-5 h-5 text-blue-600" />
          <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">
            {label}
          </span>
        </div>
        <div className="text-2xl font-bold text-gray-800">{value}</div>
        {unit && <div className="text-sm text-gray-500 mt-1">{unit}</div>}
      </div>
    );
  };

  const DiagnosisCard = ({ diagnosis, isPrimary = false }) => (
    <div
      className={`p-4 rounded-lg border-l-4 ${isPrimary
          ? "border-red-500 bg-red-50"
          : "border-orange-400 bg-orange-50"
        } hover:shadow-md transition-shadow`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h4 className="font-semibold text-gray-900">{diagnosis.condition}</h4>
          {diagnosis.code && (
            <p className="text-sm text-gray-600 mt-1">ICD: {diagnosis.code}</p>
          )}
          {diagnosis.details.prognosis && (
            <p className="text-sm text-gray-700 mt-2 italic">
              {diagnosis.details.prognosis}
            </p>
          )}
        </div>
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${isPrimary
              ? "bg-red-100 text-red-800"
              : "bg-orange-100 text-orange-800"
            }`}
        >
          {isPrimary ? "Primary" : "Secondary"}
        </span>
      </div>
    </div>
  );

  const MetricCard = ({
    icon: Icon,
    title,
    value,
    subtitle,
    color = "blue",
  }) => {
    const colorClasses = {
      blue: "bg-blue-50 border-blue-200 text-blue-800",
      green: "bg-green-50 border-green-200 text-green-800",
      purple: "bg-purple-50 border-purple-200 text-purple-800",
      orange: "bg-orange-50 border-orange-200 text-orange-800",
    };

    return (
      <div
        className={`p-6 rounded-xl border-2 ${colorClasses[color]} hover:shadow-lg transition-all duration-200`}
      >
        <div className="flex items-center space-x-3">
          <Icon className="w-8 h-8" />
          <div>
            <div className="text-2xl font-bold">{value}</div>
            <div className="text-sm font-medium">{title}</div>
            {subtitle && <div className="text-xs opacity-75">{subtitle}</div>}
          </div>
        </div>
      </div>
    );
  };


  const SectionCard = ({
    title,
    children,
    icon: Icon,
    badge = null,
    isEmpty = false,
    section = null,
    className = "",
    sectionId = null, // NEW: drives collapse
  }) => {
    const isCollapsed = sectionId ? !!collapsedSections[sectionId] : false;

    return (
      <div
        className={`overflow-hidden transition-all duration-200 ${className}`}
        style={{
          background: '#ffffff',
          borderRadius: '24px',
          border: '1px solid #eef2f8',
          marginBottom: '1.5rem',
        }}
      >
        {/* ── Header (clickable to collapse) ── */}
        <div
          className="flex-shrink-0 flex items-center justify-between"
          style={{
            padding: '1rem 1.8rem',
            background: '#ffffff',
            borderBottom: isCollapsed ? 'none' : '1px solid #f0f4fa',
            cursor: sectionId ? 'pointer' : 'default',
            userSelect: 'none',
          }}
          onClick={
            sectionId
              ? () =>
                setCollapsedSections((prev) => ({
                  ...prev,
                  [sectionId]: !prev[sectionId],
                }))
              : undefined
          }
        >
          <div className="flex items-center space-x-3">
            <Icon className="w-5 h-5" style={{ color: '#4b637a' }} />
            <h3
              className="font-semibold tracking-tight"
              style={{ fontSize: '1.1rem', color: '#1f3b4c', letterSpacing: '-0.2px' }}
            >
              {title}
            </h3>
          </div>

          {/* Stop-propagation so version/badge clicks don't collapse */}
          <div
            className="flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            {section && (
              <VersionIndicator
                section={section}
                currentVersion={getCurrentSectionVersion(section)}
                hasBeenEdited={hasSectionBeenEdited(section)}
                onShowHistory={handleShowFeedbackHistory}
              />
            )}
            {badge && (
              <span
                className="px-3 py-1 text-xs font-medium rounded-full"
                style={{ background: '#eef2ff', color: '#1f4f6e' }}
              >
                {badge}
              </span>
            )}
            {sectionId && (
              <span
                style={{ color: '#6c7e97', fontSize: '1rem', fontWeight: 600, pointerEvents: 'none' }}
              >
                {isCollapsed ? '▶' : '▼'}
              </span>
            )}
          </div>
        </div>

        {/* ── Body (hidden when collapsed) ── */}
        {!isCollapsed && (
          <div
            ref={(node) => {
              if (!sectionId) return;
              if (node) {
                sectionBodyRefs.current[sectionId] = node;
              } else {
                delete sectionBodyRefs.current[sectionId];
              }
            }}
            style={{
              padding: '1.2rem 1.8rem 1.5rem 1.8rem',
              maxHeight: sectionId ? '25rem' : undefined,
              overflowY: sectionId ? 'auto' : undefined,
              scrollbarWidth: 'thin',
            }}
          >
            {isEmpty ? (
              <div className="flex items-center justify-center py-8" style={{ color: '#6f8fae' }}>
                <AlertCircle className="w-5 h-5 mr-2" />
                <span className="italic text-sm">No data available</span>
              </div>
            ) : (
              children
            )}
          </div>
        )}
      </div>
    );
  };
  const TabButton = ({
    id,
    label,
    icon: Icon,
    isActive,
    onClick,
    count = null,
  }) => (
    <button
      onClick={() => onClick(id)}
      className={`flex items-center justify-center space-x-2 px-4 py-2.5 rounded-lg font-medium transition-all duration-200 whitespace-nowrap ${isActive
          ? "bg-blue-600 text-white shadow-lg"
          : "bg-white text-gray-700 hover:bg-blue-50 border border-gray-200"
        }`}
    >
      <Icon className="w-4 h-4 flex-shrink-0" />
      <span>{label}</span>
      {count !== null && (
        <span
          className={`px-2 py-0.5 text-xs rounded-full leading-none ${isActive ? "bg-blue-500 text-white" : "bg-gray-100 text-gray-600"
            }`}
        >
          {count}
        </span>
      )}
    </button>
  );

  // Function to highlight positive/negative findings in ROS and Physical Exam sections
  //
  // MEDICAL MEANING:
  // - POSITIVE finding = Patient HAS the symptom/condition (e.g., "(+) chest pain" = patient has chest pain)
  // - NEGATIVE finding = Patient DOES NOT have the symptom/condition (e.g., "(-) chest pain" = patient does not have chest pain)
  //
  // NOTE: A patient can have BOTH positive and negative findings in the same ROS section.
  // Example: "Cardiovascular: (+) chest pain, (-) palpitations" = patient HAS chest pain but DOES NOT have palpitations
  //
  // COLORS:
  // - GREEN = Positive findings (symptoms/conditions PRESENT - patient has them)
  // - RED = Negative findings (symptoms/conditions ABSENT - patient does not have them)
  const POSITIVE_COLOR = "#16a34a"; // Green-600 for positive findings (patient HAS symptoms/conditions)
  const NEGATIVE_COLOR = "#dc2626"; // Red-600 for negative findings (patient DOES NOT have symptoms/conditions)

  const highlightFindings = (text, isROS = false, isPhysicalExam = false) => {
    if (!isROS && !isPhysicalExam) return text;

    let highlighted = text;

    // POSITIVE FINDINGS (GREEN) - Patient HAS the symptom/condition
    // Examples: "(+) chest pain" = patient has chest pain, "Reports fever" = patient has fever
    const positivePatterns = [
      // (+) pattern - e.g., "(+) photophobia", "(+) chest pain" = patient HAS these symptoms
      /\((\+)\)\s*([^,;•\n]+?)(?=\s*[,;•\n]|$)/gi,
      // "positive" or "positive for" - e.g., "positive for chest pain" = patient HAS chest pain
      /\b(positive(?:\s+for)?)\s+([^,;•\n]+?)(?=\s*[,;•\n]|$)/gi,
      // "Reports" - e.g., "Reports occasional high blood pressure" = patient HAS high blood pressure
      // This is VERY COMMON in actual API responses - highlight the entire phrase
      /\b(reports?)\s+([^,;•\n]+?)(?=\s*[,;•\n]|$)/gi,
      // "Takes [medication] for [condition]" - e.g., "Takes montelukast for allergies" = patient HAS allergies
      /\b(takes?)\s+([^,;•\n]+?\s+for\s+[^,;•\n]+?)(?=\s*[,;•\n]|$)/gi,
      // Condition + "managed with" or "treated with" - e.g., "Anxiety managed with alprazolam" = patient HAS anxiety
      /\b(anxiety|depression|hypertension|high\s+blood\s+pressure|toenail\s+fungus|onychomycosis|allergies|allergic\s+rhinitis)\s+(managed|treated)\s+with/gi,
      // Abnormal findings - e.g., "abnormal heart sounds" = patient HAS abnormal findings
      /\b(abnormal)\s+([^,;•\n]+?)(?=\s*[,;•\n]|$)/gi,
      // Elevated/decreased - e.g., "elevated blood pressure" = patient HAS elevated BP
      /\b(elevated|decreased)\s+([^,;•\n]+?)(?=\s*[,;•\n]|$)/gi,
    ];

    // NEGATIVE FINDINGS (RED) - Patient DOES NOT have the symptom/condition
    // Examples: "(-) chest pain" = patient does NOT have chest pain, "No fever" = patient does NOT have fever
    const negativePatterns = [
      // (-) pattern - e.g., "(-) fever", "(-) chest pain" = patient DOES NOT have these symptoms
      /\((-)\)\s*([^,;•\n]+?)(?=\s*[,;•\n]|$)/gi,
      // "negative" or "negative for" - e.g., "negative for chest pain" = patient DOES NOT have chest pain
      /\b(negative(?:\s+for)?)\s+([^,;•\n]+?)(?=\s*[,;•\n]|$)/gi,
      // "denies" - e.g., "denies fever", "denies chest pain" = patient DOES NOT have these symptoms
      /\b(denies)\s+([^,;•\n]+?)(?=\s*[,;•\n]|$)/gi,
      // "No" followed by finding - e.g., "No weight changes", "No chest pain" = patient DOES NOT have these
      // This is VERY COMMON in actual API responses - match "No" + any text until comma/semicolon/end
      /\b(no)\s+(weight\s+changes|issues\s+reported|[^,;•\n]+?)(?=\s*[,;•\n]|$)/gi,
    ];

    // Apply positive finding highlighting
    positivePatterns.forEach((pattern) => {
      highlighted = highlighted.replace(
        pattern,
        (match, indicator, finding) => {
          // Skip if already wrapped in HTML tags
          if (match.includes("<span") || match.includes("style=")) {
            return match;
          }
          // Handle different pattern formats
          let fullText;
          if (indicator && finding) {
            fullText = `${indicator} ${finding}`.trim();
          } else if (indicator) {
            // For patterns like "anxiety managed with" where indicator is the condition
            fullText = match.trim();
          } else {
            fullText = match.trim();
          }
          if (fullText) {
            return `<span style="color: ${POSITIVE_COLOR}; font-weight: bold;">${fullText}</span>`;
          }
          return match;
        },
      );
    });

    // Additional pattern for ROS: Symptoms listed directly after system names (without explicit markers)
    // Examples: "ENT: Vertigo, nausea" = patient HAS vertigo and nausea
    //          "Musculoskeletal: Neck and back pain, carpal tunnel syndrome" = patient HAS these
    //          "Neurological: Headaches, vertigo" = patient HAS these
    // This pattern captures text after system name colon that is NOT "Not documented"
    if (isROS) {
      // Match system name followed by colon, then capture symptoms (excluding "Not documented")
      highlighted = highlighted.replace(
        /(?:^|\n|•|-)\s*(?:\*\*)?(?:HEENT|Eyes\/Ears\/Nose\/Throat|Cardiovascular|Respiratory|Gastrointestinal|Genitourinary|Musculoskeletal|Neurological|Psychiatric|Endocrine|Hematologic|Hematologic\/Lymphatic|Allergic|Allergic\/Immunologic|Skin|Integumentary|Constitutional|Reproductive|ENT|Eyes):\s*((?:(?!Not\s+documented)[^,;•\n])+?)(?=\s*[,;•\n]|$)/gi,
        (match, symptoms) => {
          // Skip if already highlighted or if it's "Not documented"
          if (
            match.includes("<span") ||
            match.includes("style=") ||
            match.includes("Not documented")
          ) {
            return match;
          }
          // Skip if symptoms are empty or just whitespace
          if (!symptoms || !symptoms.trim()) {
            return match;
          }
          // Highlight the symptoms part (keep the system name unhighlighted)
          return match.replace(
            symptoms,
            `<span style="color: ${POSITIVE_COLOR}; font-weight: bold;">${symptoms.trim()}</span>`,
          );
        },
      );
    }

    // Apply negative finding highlighting (only for ROS section)
    if (isROS) {
      negativePatterns.forEach((pattern) => {
        highlighted = highlighted.replace(
          pattern,
          (match, indicator, finding) => {
            // Skip if already wrapped in HTML tags
            if (match.includes("<span") || match.includes("style=")) {
              return match;
            }
            // Handle different pattern formats
            let fullText;
            if (indicator && finding) {
              fullText = `${indicator} ${finding}`.trim();
            } else {
              fullText = match.trim();
            }
            if (fullText) {
              return `<span style="color: ${NEGATIVE_COLOR}; font-weight: bold;">${fullText}</span>`;
            }
            return match;
          },
        );
      });
    }

    // Apply Physical Exam finding highlighting
    // Split into positive (green) and negative (red) patterns
    if (isPhysicalExam) {
      // NEGATIVE Physical Exam findings (RED) - Patient DOES NOT have these exam findings
      // Examples: "no murmurs" = patient does NOT have murmurs, "no wheezes" = patient does NOT have wheezes
      const negativeExamPatterns = [
        // "no" followed by specific exam findings - e.g., "no murmurs", "no wheezes", "no tenderness"
        /\b(no)\s+(murmurs|wheezes|rales|rhonchi|tenderness|edema|rash|lesions|masses|organomegaly|lymphadenopathy|nystagmus|diplopia|abnormal\s+findings)/gi,
      ];

      // POSITIVE Physical Exam findings (GREEN) - Patient HAS these exam findings
      // Examples: "abnormal heart sounds" = patient HAS abnormal sounds, "tenderness present" = patient HAS tenderness
      const positiveExamPatterns = [
        // Abnormal exam findings - e.g., "abnormal heart sounds", "abnormal lung sounds" = patient HAS abnormal findings
        /\b(abnormal)\s+(heart\s+sounds?|lung\s+sounds?|bowel\s+sounds?|reflexes?|findings?)/gi,
        // Positive exam findings - e.g., "tenderness present", "edema noted" = patient HAS these findings
        /\b(tenderness|edema|rash|lesion|mass|murmur|wheeze|rales|rhonchi)\s+(present|noted|detected|found)/gi,
      ];

      // Apply negative exam findings (red)
      negativeExamPatterns.forEach((pattern) => {
        highlighted = highlighted.replace(
          pattern,
          (match, indicator, finding) => {
            // Skip if already wrapped in HTML tags
            if (match.includes("<span") || match.includes("style=")) {
              return match;
            }
            const fullText = `${indicator} ${finding}`.trim();
            return `<span style="color: ${NEGATIVE_COLOR}; font-weight: bold;">${fullText}</span>`;
          },
        );
      });

      // Apply positive exam findings (green)
      positiveExamPatterns.forEach((pattern) => {
        highlighted = highlighted.replace(
          pattern,
          (match, indicator, finding) => {
            // Skip if already wrapped in HTML tags
            if (match.includes("<span") || match.includes("style=")) {
              return match;
            }
            const fullText = `${indicator} ${finding}`.trim();
            return `<span style="color: ${POSITIVE_COLOR}; font-weight: bold;">${fullText}</span>`;
          },
        );
      });
    }

    return highlighted;
  };

  // Enhanced paragraph formatter for better readability
  const formatAsParagraphs = (content, sectionType = null) => {
    if (!content) return "<p><em>No content available</em></p>";
    content = content.replace(
    /([^\n])\n(?=\s*(?:\*\*)?Plan:\s*(?:\*\*)?)/g,
    "$1\n\n",
  );

    // Check if this is HPI content
    const isHPIContent =
      content.includes("**HPI:**") ||
      content.includes("HPI:") ||
      (content.includes("presents with") && content.length > 100);

    // Detect ROS section - ONLY highlight content that is explicitly within ROS section
    // ROS detection must be strict to avoid highlighting HPI, PMH, or other subjective subsections

    // 1. Check for explicit ROS header
    const hasROSHeader =
      /(?:^|\n|•|-)\s*(?:\*\*)?ROS(?:\*\*)?:|(?:^|\n|•|-)\s*Review of Systems:/i.test(
        content,
      ) ||
      content.includes("**ROS:**") ||
      content.includes("ROS:") ||
      content.includes("Review of Systems:");

    // 2. Check for ROS system names (these are specific to ROS section only)
    // ROS system names: Constitutional, Cardiovascular, HEENT, Respiratory, etc.
    const hasROSSystemNames =
      /(?:^|\n|•|-)\s*(?:\*\*)?(?:HEENT|Eyes\/Ears\/Nose\/Throat|Cardiovascular|Respiratory|Gastrointestinal|Genitourinary|Musculoskeletal|Neurological|Psychiatric|Endocrine|Hematologic|Hematologic\/Lymphatic|Allergic|Allergic\/Immunologic|Skin|Integumentary|Constitutional|Reproductive):/i.test(
        content,
      );

    // 3. Check for (+) or (-) patterns (these are specific to ROS findings)
    const hasROSFindingSymbols = /\([+-]\)/.test(content);

    // 4. ROS is detected ONLY if:
    //    - Has explicit ROS header, OR
    //    - Has ROS system names (Constitutional:, Cardiovascular:, etc.), OR
    //    - Has (+) or (-) finding symbols, OR
    //    - Explicitly marked as ROS section
    // Do NOT use generic "Reports/Denies/No" patterns for detection - only for highlighting after ROS is confirmed
    const isROS =
      hasROSHeader ||
      hasROSSystemNames ||
      hasROSFindingSymbols ||
      sectionType === "ros";

    // Detect Physical Exam section - look for Physical Exam header or exam patterns
    const hasPhysicalExamHeader =
      /(?:^|\n|•)\s*(?:Physical Exam|Examination):/i.test(content) ||
      content.includes("**Physical Exam:**") ||
      content.includes("Physical Exam:") ||
      content.includes("Examination:");

    // Detect Physical Exam patterns: "no murmurs", "no tenderness", "normal", exam system names
    // Also check if we're in objective section and content has Physical Exam patterns
    const hasPhysicalExamPatterns =
      /(?:Heart|Lungs|Abdomen|Extremities|Neurological|Skin|HEENT|Neck|Cardiovascular|Respiratory):|(?:no\s+(?:murmurs|wheezes|tenderness|edema|rash|lesions))|(?:normal|abnormal)\s+(?:heart|lung|abdomen)/i.test(
        content,
      );

    // If in objective section and has Physical Exam patterns, treat as Physical Exam
    const isPhysicalExam =
      hasPhysicalExamHeader ||
      hasPhysicalExamPatterns ||
      sectionType === "physicalExam" ||
      (sectionType === "objective" && hasPhysicalExamPatterns);

    // Apply highlighting BEFORE markdown conversion (work with plain text)
    let contentToFormat = content;
    if (isROS) {
      contentToFormat = highlightFindings(contentToFormat, true, false);
    }
    if (isPhysicalExam) {
      contentToFormat = highlightFindings(contentToFormat, false, true);
    }

    // Showcase-only: ROS system subheadings often lack list markers; add "-" so
    // the formatter below turns them into consistent bullet points (display only).
    if (isROS && (sectionType === "subjective" || sectionType === null)) {
      const rosSystemSubheading =
        /^(?!\s*[•\-])(\s*)((?:\*\*)?(?:HEENT|Eyes\/Ears\/Nose\/Throat|Cardiovascular|Respiratory|Gastrointestinal|Genitourinary|Musculoskeletal|Neurological|Psychiatric|Endocrine|Hematologic|Hematologic\/Lymphatic|Allergic|Allergic\/Immunologic|Skin|Integumentary|Constitutional|Reproductive)(?:\*\*)?:)/gim;
      contentToFormat = contentToFormat.replace(
        rosSystemSubheading,
        (_, indent, label) => `${indent}- ${label}`,
      );
    }

    const soapSectionHeadingPattern =
      sectionType === "assessment"
        ? // Do not promote "Plan" to an h3 in Assessment — integrated **Plan:** is a subheading.
        /(^|\n)(Subjective|Objective|Assessment & Plan)(\n|:)/gi
        : /(^|\n)(Subjective|Objective|Assessment & Plan|Assessment|Plan)(\n|:)/gi;

    let formatted = contentToFormat
      // 1. Bold ICD headings: **Label (X00.0):** / **Label (X00.0)** / **Label (X00.0)**:
      .replace(
        /\*\*([^*]+?)\s*\(\s*([A-Z]\d{2}(?:\.\d{1,3})?(?:\s*,\s*[A-Z]\d{2}(?:\.\d{1,3})?)*)\s*\)\s*:?\s*\*\*:?/g,
        (match, label, codes) => {
          const codeList = codes.split(/\s*,\s*/);
          const primaryCode = codeList[0].trim().toUpperCase();
          const allCodesDisplay = codeList.map(c => c.trim().toUpperCase()).join(", ");
          return `<strong class="icd-heading-clickable" data-icd-code="${primaryCode}" data-icd-label="${label.trim()}" style="cursor:pointer;text-decoration:underline dotted;text-underline-offset:3px;" title="Click to edit ICD code ${allCodesDisplay}">${label.trim()} (${allCodesDisplay}):</strong>`;
        }
      )
      // 2. Non-bold ICD headings (no ** markers): Label (X00.0, Y00.0)
      .replace(
        /^([A-Z][^*\n(]+?)\s*\(\s*([A-Z]\d{2}(?:\.\d{1,3})?(?:\s*,\s*[A-Z]\d{2}(?:\.\d{1,3})?)*)\s*\)(?:\s*\/\/[^\n]*)?\s*$/gm,
        (match, label, codes) => {
          const trimmedLabel = label.trim();
          // Skip if it looks like a plan bullet or follow-up line
          if (/^[-•*]/.test(trimmedLabel) || trimmedLabel.length < 5) return match;
          const codeList = codes.split(/\s*,\s*/);
          const primaryCode = codeList[0].trim().toUpperCase();
          const allCodesDisplay = codeList.map(c => c.trim().toUpperCase()).join(", ");
          return `<strong class="icd-heading-clickable" data-icd-code="${primaryCode}" data-icd-label="${trimmedLabel}" style="cursor:pointer;text-decoration:underline dotted;text-underline-offset:3px;" title="Click to edit ICD code ${allCodesDisplay}">${trimmedLabel} (${allCodesDisplay})</strong>`;
        }
      )
      // 3. Generic bold — must come AFTER the ICD-specific replacements

      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(soapSectionHeadingPattern, "$1<h3>$2</h3>")
      .replace(
        /(^|\n)(CC|HPI|ROS|PMH|Surgical History|Hospitalization History|Medications|Medication Intolerance|Allergies|Social History|Family History|Vital Signs|Physical Exam|Lab Results|Imaging\/Studies|Other Data|Patient Education|Follow-up|Goals|Contingency Plan)(:)/gi,
        "$1• <strong>$2:</strong>",
      )
      .replace(/(^|\n)-\s*/g, "$1• ");

    // For HPI content, convert double newlines to triple <br> for extra spacing
    if (isHPIContent) {
      formatted = formatted.replace(/([.!?:)"'])[ \t]*\n(?=\s*[A-Z])/g,"$1\n\n",);
      formatted = formatted.replace(/\n\s*\n/g, "<br><br><br>");
    } else {
      formatted = formatted.replace(/\n\s*\n/g, "<br><br>");
    }

    formatted = formatted.replace(/\n/g, "<br>");

    const html = `<p>${formatted.trim()}</p>`;
    // Future: practice-partner highlight / trusted|external badges
    // const skipNames = [
    //   resolveDisplayName(currentUser, ""),
    //   `${currentUser?.firstName || ""} ${currentUser?.lastName || ""}`.trim(),
    //   currentUser?.profile?.firstName && currentUser?.profile?.lastName
    //     ? `${currentUser.profile.firstName} ${currentUser.profile.lastName}`
    //     : "",
    // ].filter(Boolean);
    // return annotatePracticePartnersInHtml(html, practicePartners, {
    //   skipNames,
    // });
    return html;
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 px-3 sm:px-6 pb-40 md:pb-6 pt-2"
      // Future: practice-partner highlight / trusted|external badges
      // onClick={handlePartnerMentionInteraction}
      // onMouseOver={handlePartnerMentionInteraction}
      // onMouseOut={handlePartnerMentionMouseOut}
    >
      {isScriber && isAssignedSOAPNote && (
        <div className="mb-6 bg-blue-50 border-l-4 border-blue-400 p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-blue-400 mr-3" />
            <div>
              <p className="text-sm font-medium text-blue-800">
                Assigned SOAP Note
              </p>
              <p className="text-xs text-blue-600 mt-1">
                This SOAP note has been assigned to you for review and editing.
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto space-y-0">
        {/* Enhanced Header with Metadata */}
        {/* <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 text-white p-6 md:p-8 relative">
            <h1 className="text-3xl md:text-4xl font-bold">
              SOAP Documentation
            </h1>


          </div>
        </div> */}

        {/* Navigation Tabs */}
        <div className="flex flex-nowrap sm:flex-wrap gap-2 sm:gap-3 justify-start overflow-x-auto pb-2 sm:overflow-visible">
          {/* <TabButton
            id="overview"
            label="Clinical Overview"
            icon={Stethoscope}
            isActive={activeSection === "overview"}
            onClick={setActiveSection}
          /> */}
          {/* Assign / Re-assign Button – Only for Admin */}
          {/*{isAdmin &&
            (soapNote?.status === "draft" ||
              soapNote?.status === "assigned" ||
              soapNote?.status === "revision_requested") && (
              <button
                onClick={handleAssignClick}
                disabled={
                  loadingAssignableUsers || assignmentStatus.isAssigning
                }
                className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ml-auto ${
                  loadingAssignableUsers || assignmentStatus.isAssigning
                    ? "bg-gray-400 cursor-not-allowed"
                    : isSOAPNoteAssigned
                      ? "bg-orange-600 hover:bg-orange-700 text-white"
                      : "bg-green-600 hover:bg-green-700 text-white"
                }`}
              >
                {loadingAssignableUsers || assignmentStatus.isAssigning ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                    {assignmentStatus.isAssigning
                      ? "Assigning..."
                      : "Loading..."}
                  </>
                ) : (
                  <>
                    {isSOAPNoteAssigned ? (
                      <>
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                          />
                        </svg>
                        Re-assign
                      </>
                    ) : (
                      <>
                        <Users2 className="w-4 h-4" />
                        Assign to Scribe
                      </>
                    )}
                  </>
                )}
              </button>
            )} */}
          {/* Show back button for assigned SOAP notes */}
          {/* {shouldShowBackButton && backButtonConfig && (
            <TabButton
              id="back"
              label={backButtonConfig.label}
              icon={User}
              isActive={activeSection === "overview"}
              onClick={() => navigate(backButtonConfig.path)}
            />
          )} */}
          {/* <TabButton
            id="vitals"
            label="Vital Signs"
            icon={Activity}
            isActive={activeSection === "vitals"}
            onClick={setActiveSection}
            count={Object.values(advancedData.vitals).filter((v) => v).length}
          />

          <TabButton
            id="diagnostics"
            label="Diagnostics"
            icon={ClipboardList}
            isActive={activeSection === "diagnostics"}
            onClick={setActiveSection}
            count={advancedData.diagnostics.totalDiagnoses}
          /> */}
        </div>

        {/* Dynamic Content Based on Active Section */}
        {activeSection === "overview" && (
          <div className="space-y-8">
            {/* Patient Information */}
            <SectionCard title="Patient Overview" icon={User}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Add Patient Name Card */}
                <MetricCard
                  icon={Shield}
                  title="Risk Level"
                  value={advancedData.riskAssessment.riskLevel}
                  color={
                    advancedData.riskAssessment.riskLevel === "High"
                      ? "orange"
                      : "green"
                  }
                />
                <MetricCard
                  icon={TrendingUp}
                  title="Complexity"
                  value={advancedData.clinicalInsights.complexity}
                  color="blue"
                />
              </div>
              {advancedData.patientInfo.chiefComplaint && (
                <div className="mt-6 p-4 bg-red-50 border-l-4 border-red-400 rounded-r-lg">
                  <h4 className="font-semibold text-red-800 mb-2">
                    Chief Complaint
                  </h4>
                  <p className="text-red-700">
                    {advancedData.patientInfo.chiefComplaint}
                  </p>
                  {advancedData.patientInfo.duration && (
                    <p className="text-sm text-red-600 mt-1">
                      Duration: {advancedData.patientInfo.duration}
                    </p>
                  )}
                </div>
              )}
            </SectionCard>

            {/* Allergies & Medical History */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <SectionCard
                title="Allergies"
                icon={Siren}
                isEmpty={
                  !advancedData.patientInfo.allergies ||
                  advancedData.patientInfo.allergies.length === 0
                }
              >
                {advancedData.patientInfo.allergies &&
                  advancedData.patientInfo.allergies.length > 0 && (
                    <div className="space-y-3">
                      {advancedData.patientInfo.allergies.map(
                        (allergy, index) => (
                          <div
                            key={index}
                            className="flex items-center space-x-3 p-3 bg-red-50 border border-red-200 rounded-lg"
                          >
                            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                            <span className="text-red-800 font-medium">
                              {allergy}
                            </span>
                          </div>
                        ),
                      )}
                    </div>
                  )}
              </SectionCard>

              <SectionCard
                title="Medical History"
                icon={History}
                isEmpty={
                  !advancedData.patientInfo.medicalHistory ||
                  advancedData.patientInfo.medicalHistory.length === 0
                }
              >
                {advancedData.patientInfo.medicalHistory &&
                  advancedData.patientInfo.medicalHistory.length > 0 && (
                    <div className="space-y-2">
                      {advancedData.patientInfo.medicalHistory.map(
                        (item, index) => (
                          <div
                            key={index}
                            className="flex items-start space-x-3 p-3 bg-blue-50 border border-blue-200 rounded-lg"
                          >
                            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-blue-800">{item}</span>
                          </div>
                        ),
                      )}
                    </div>
                  )}
              </SectionCard>
            </div>

            {/* Social History */}
            {advancedData.patientInfo.socialHistory &&
              Object.keys(advancedData.patientInfo.socialHistory).length >
              0 && (
                <SectionCard title="Social History" icon={Users}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Object.entries(advancedData.patientInfo.socialHistory).map(
                      ([key, value]) => (
                        <div
                          key={key}
                          className="p-4 bg-green-50 border border-green-200 rounded-lg"
                        >
                          <div className="text-sm text-green-600 font-medium uppercase tracking-wider">
                            {key}
                          </div>
                          <div className="text-green-800 mt-1">{value}</div>
                        </div>
                      ),
                    )}
                  </div>
                </SectionCard>
              )}
          </div>
        )}

        {activeSection === "vitals" && (
          <div className="space-y-6">
            <SectionCard
              title="Vital Signs Monitor"
              icon={Activity}
              isEmpty={
                Object.values(advancedData.vitals).filter((v) => v).length === 0
              }
            >
              {Object.values(advancedData.vitals).filter((v) => v).length >
                0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {advancedData.vitals.bloodPressure && (
                      <VitalSignCard
                        icon={Heart}
                        label="Blood Pressure"
                        value={advancedData.vitals.bloodPressure}
                        unit="mmHg"
                      />
                    )}
                    {advancedData.vitals.heartRate && (
                      <VitalSignCard
                        icon={Activity}
                        label="Heart Rate"
                        value={advancedData.vitals.heartRate}
                        unit="bpm"
                      />
                    )}
                    {advancedData.vitals.temperature && (
                      <VitalSignCard
                        icon={Thermometer}
                        label="Temperature"
                        value={advancedData.vitals.temperature}
                        unit="°F"
                      />
                    )}
                    {advancedData.vitals.respiratoryRate && (
                      <VitalSignCard
                        icon={Wind}
                        label="Respiratory Rate"
                        value={advancedData.vitals.respiratoryRate}
                        unit="breaths/min"
                      />
                    )}
                    {advancedData.vitals.oxygenSaturation && (
                      <VitalSignCard
                        icon={Droplets}
                        label="Oxygen Saturation"
                        value={advancedData.vitals.oxygenSaturation}
                        unit="%"
                      />
                    )}
                    {advancedData.vitals.weight && (
                      <VitalSignCard
                        icon={TrendingUp}
                        label="Weight"
                        value={advancedData.vitals.weight}
                      />
                    )}
                    {advancedData.vitals.height && (
                      <VitalSignCard
                        icon={TrendingUp}
                        label="Height"
                        value={advancedData.vitals.height}
                      />
                    )}
                  </div>
                )}
            </SectionCard>
          </div>
        )}

        {/* {activeSection === "soap" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SectionCard
              title="Subjective"
              icon={User}
              isEmpty={!parsedSections.subjective}
            >
              {parsedSections.subjective && (
                <div
                  className="prose prose-sm max-w-none text-gray-700"
                  dangerouslySetInnerHTML={{
                    __html: formatAsParagraphs(parsedSections.subjective),
                  }}
                />
              )}
            </SectionCard>

            <SectionCard
              title="Objective"
              icon={Eye}
              isEmpty={!parsedSections.objective}
            >
              {parsedSections.objective && (
                <div
                  className="prose prose-sm max-w-none text-gray-700"
                  dangerouslySetInnerHTML={{
                    __html: formatAsParagraphs(parsedSections.objective),
                  }}
                />
              )}
            </SectionCard>

            <SectionCard
              title="Assessment & Plan"
              icon={ClipboardList}
              isEmpty={!parsedSections.assessment && !parsedSections.plan}
            >
              {parsedSections.assessment && (
                <div
                  className="prose prose-sm max-w-none mb-6"
                  dangerouslySetInnerHTML={{
                    __html: formatAsParagraphs(
                      parsedSections.assessment,
                      "assessment",
                    ),
                  }}
                />
              )}

              {parsedSections.plan && (
                <div className="prose prose-sm max-w-none">
                  <h3 className="text-gray-900 font-semibold mb-2">Plan</h3>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: formatAsParagraphs(parsedSections.plan),
                    }}
                  />
                </div>
              )}
            </SectionCard>

          </div>
        )} */}

        {activeSection === "soap" && (
          <div className="space-y-6 md:mr-8">
            {templateRegenerating && (
              <div className="rounded-lg border border-blue-200 bg-blue-50/90 px-4 py-3 flex items-center gap-3 animate-in fade-in duration-200">
                <div className="w-5 h-5 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-blue-900 truncate">
                    {templateRegenLabel
                      ? `Updating note with “${templateRegenLabel}”…`
                      : "Updating note with new template…"}
                  </p>
                  <p className="text-xs text-blue-700 mt-0.5">
                    Regenerating from transcript — you can keep browsing; this
                    usually finishes shortly.
                  </p>
                </div>
              </div>
            )}
            {/* {!isReadOnly &&
              soapNote &&
              (soapNote.subjective || soapNote.rawContent) && (
                <SoapNotePronounBar
                  soapNote={soapNote}
                  patientContext={patientContext}
                  aiService={aiService}
                  onStartProcessing={() => {
                    setSoapSectionLoading({
                      subjective: true,
                      objective: true,
                      assessment: true,
                      plan: true,
                    });
                  }}
                  onEndProcessing={() => {
                    setSoapSectionLoading({
                      subjective: false,
                      objective: false,
                      assessment: false,
                      plan: false,
                    });
                  }}
                  onUpdateSoapNote={async (updatedSoapNote) => {
                    setSoapNote(updatedSoapNote);
                  }}
                />
              )} */}

            {/* SOAP Note Assignment & Actions 
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 shadow-sm mb-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100">
                    <ClipboardList className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      SOAP Note Assignment
                    </h3>
                    {isAdmin && (
                      <div className="flex flex-wrap items-center gap-4 mt-1">
                        {/* Status 
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <FileStack className="w-4 h-4" />
                          <span>
                            Status:{" "}
                            <span className="font-medium capitalize">
                              {soapNote?.status?.replace("_", " ") || "unknown"}
                            </span>
                          </span>
                        </div>

                        {/* Assigned To 
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <UsersRound className="w-4 h-4" />
                          <span>
                            Assigned To:{" "}
                            <span className="font-medium capitalize">
                              {soapNote?.assignedUser?.profile
                                ? `${soapNote.assignedUser.profile.firstName || ""} ${soapNote.assignedUser.profile.lastName || ""}`.trim() ||
                                  soapNote.assignedUser.email
                                : soapNote?.assignedUser?.email || "Unassigned"}
                            </span>
                          </span>
                        </div>

                        {/* Created By 
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <UserCheck className="w-4 h-4" />
                          <span>
                            Created By:{" "}
                            <span className="font-medium capitalize">
                              {getCreatedByLabel()}
                            </span>
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Admin Actions - Show for admins 
                {isAdmin && (
                  <div className="flex flex-wrap gap-2">
                    {/* For revision_requested status, show Under Review (disabled) 
                    {soapNote?.status === "revision_requested" && (
                      <button
                        disabled
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg cursor-not-allowed flex items-center gap-2"
                      >
                        <Clock className="w-4 h-4" />
                        Under Review
                      </button>
                    )}
                    {/* Pending Review - Show Under Review button (disabled) 
                    {soapNote?.status === "pending_review" && (
                      <button
                        disabled
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg cursor-not-allowed flex items-center gap-2"
                      >
                        <Clock className="w-4 h-4" />
                        Under Review
                      </button>
                    )}

                    {/* Approved - Show Green Done button (disabled) 
                    {soapNote?.status === "approved" && (
                      <button
                        disabled
                        className="px-4 py-2 bg-green-600 text-white rounded-lg cursor-not-allowed flex items-center gap-2"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Approved
                      </button>
                    )}
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  {/* Scribe Actions 
                  {isScriber &&
                    soapNote?.assignedTo === currentUser?.userId && (
                      <>
                        {/* Draft or Revision Requested - Show Submit/Resubmit Button 
                        {(soapNote?.status === "draft" ||
                          soapNote?.status === "revision_requested" ||
                          soapNote?.status === "assigned") && (
                          <button
                            onClick={() => setShowSubmitReviewModal(true)}
                            disabled={actionLoading}
                            className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                              actionLoading
                                ? "bg-gray-400 cursor-not-allowed"
                                : soapNote?.status === "revision_requested"
                                  ? "bg-orange-600 hover:bg-orange-700 text-white"
                                  : "bg-blue-600 hover:bg-blue-700 text-white"
                            }`}
                          >
                            <CheckCircle className="w-4 h-4" />
                            {soapNote?.status === "revision_requested"
                              ? "Resubmit for Review"
                              : "Submit for Review"}
                          </button>
                        )}

                        {/* Pending Review - Show Awaiting Review
                        {soapNote?.status === "pending_review" && (
                          <div className="flex items-center gap-2">
                            <Clock className="w-5 h-5 text-blue-600" />
                            <span className="text-sm text-blue-700 font-medium">
                              Awaiting admin review
                            </span>
                          </div>
                        )}

                        {/* Approved - Show Approved Status 
                        {soapNote?.status === "approved" && (
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <span className="text-sm text-green-700 font-medium">
                              Approved
                            </span>
                          </div>
                        )}
                      </>
                    )}
                    

                  {/* Admin Actions
                  {isAdmin && soapNote?.status === "pending_review" && (
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() =>
                          setShowAdminActionModal({
                            isOpen: true,
                            action: "approve",
                          })
                        }
                        disabled={actionLoading}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:bg-green-400"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Approve
                      </button>

                      <button
                        onClick={() =>
                          setShowAdminActionModal({
                            isOpen: true,
                            action: "request_revision",
                          })
                        }
                        disabled={actionLoading}
                        className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2 disabled:bg-orange-400"
                      >
                        <Edit3 className="w-4 h-4" />
                        Request Revisions
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Show revision details if requested 
              {soapNote?.status === "revision_requested" &&
                soapNote?.revisionComments && (
                  <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-orange-800">
                          Revision Required
                        </h4>
                        <p className="text-sm text-orange-700 mt-1">
                          {soapNote.revisionComments}
                        </p>
                        {soapNote.revisionDeadline && (
                          <p className="text-xs text-orange-600 mt-1">
                            Deadline:{" "}
                            {new Date(
                              soapNote.revisionDeadline,
                            ).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

              {/* Show approval details if approved 
              {soapNote?.status === "approved" &&
                soapNote?.approvalComments && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-green-800">
                          Approval Comments
                        </h4>
                        <p className="text-sm text-green-700 mt-1">
                          {soapNote.approvalComments}
                        </p>
                        <p className="text-xs text-green-600 mt-1">
                          Approved on:{" "}
                          {new Date(soapNote.approvedAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
            </div>
            */}

            {/* ── SINGLE BIG BOX ── */}
            <div
              className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-visible"
              data-soap-sections-container
              data-tour="soap-section"
            >
              {/* ========== SUBJECTIVE ========== */}
              <div className="border-b border-gray-100 last:border-b-0">
                {/* Header (clickable) */}
                <div
                  className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-4 sm:p-6 cursor-pointer select-none sticky top-0 bg-white z-10"
                  onClick={() =>
                    setCollapsedSections((prev) => ({
                      ...prev,
                      subjective: !prev.subjective,
                    }))
                  }
                >
                  <div className="flex items-center gap-2">
                    {collapsedSections.subjective ? (
                      <ChevronRight size={18} className="text-gray-600" />
                    ) : (
                      <ChevronDown size={18} className="text-gray-600" />
                    )}
                    <h3 className="text-xl font-semibold text-gray-900">
                      Subjective
                    </h3>
                  </div>
                  <div
                    className="flex items-center gap-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <VersionIndicator
                      section="subjective"
                      currentVersion={getCurrentSectionVersion("subjective")}
                      hasBeenEdited={hasSectionBeenEdited("subjective")}
                      onShowHistory={handleShowFeedbackHistory}
                    />
                    {(() => {
                      const content = sectionContent.subjective || "";
                      const bullets = splitBullets(content);
                      const isBulletPoints = bullets.length > 1;
                      const currentFeedback =
                        getSectionFeedbackState("subjective");
                      return (
                        <>
                          <button
                            onClick={() =>
                              !isReadOnly &&
                              setFeedbackModal({
                                isOpen: true,
                                section: "subjective",
                                type: "like",
                                content: "",
                                contentType: "entire-section",
                              })
                            }
                            disabled={currentFeedback !== null || isReadOnly}
                            className={`p-2 rounded-full transition ${currentFeedback === "like"
                                ? "bg-green-100 cursor-default"
                                : currentFeedback !== null
                                  ? "bg-gray-100 cursor-not-allowed"
                                  : "hover:bg-green-100"
                              }`}
                            title={
                              isReadOnly
                                ? "SOAP note is approved and read-only"
                                : currentFeedback === "like"
                                  ? "Already liked this section"
                                  : currentFeedback !== null || isReadOnly
                                    ? "Feedback already submitted for this section"
                                    : `Like this ${isBulletPoints
                                      ? "bullet points section"
                                      : "paragraph section"
                                    }`
                            }
                          >
                            {currentFeedback === "like" ? (
                              <ThumbsUp
                                fill="#17803C"
                                className="w-5 h-5 text-green-600"
                              />
                            ) : currentFeedback !== null || isReadOnly ? (
                              <ThumbsUp className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ThumbsUp className="w-5 h-5 text-gray-600 hover:text-green-600" />
                            )}
                          </button>

                          <button
                            onClick={() =>
                              !isReadOnly &&
                              setFeedbackModal({
                                isOpen: true,
                                section: "subjective",
                                type: "dislike",
                                content: "",
                                contentType: "entire-section",
                              })
                            }
                            disabled={currentFeedback !== null || isReadOnly}
                            className={`p-2 rounded-full transition ${currentFeedback === "dislike"
                                ? "bg-red-100 cursor-default"
                                : currentFeedback !== null
                                  ? "bg-gray-100 cursor-not-allowed"
                                  : "hover:bg-red-100"
                              }`}
                            title={
                              isReadOnly
                                ? "SOAP note is approved and read-only"
                                : currentFeedback === "dislike"
                                  ? "Already disliked this section"
                                  : currentFeedback !== null
                                    ? "Feedback already submitted for this section"
                                    : `Dislike this ${isBulletPoints
                                      ? "bullet points section"
                                      : "paragraph section"
                                    }`
                            }
                          >
                            {currentFeedback === "dislike" ? (
                              <ThumbsDown
                                fill="#dc2626"
                                className="w-5 h-5 text-red-600"
                              />
                            ) : currentFeedback !== null || isReadOnly ? (
                              <ThumbsDown className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ThumbsDown className="w-5 h-5 text-gray-600 hover:text-red-600" />
                            )}
                          </button>

                          {/* Copy Button */}
                          {copiedSection === "subjective" ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : (
                            <Copy
                              className="w-6 h-6 cursor-pointer text-gray-600 hover:text-gray-800"
                              onClick={() =>
                                handleCopyClick("subjective", content)
                              }
                              title={`Copy ${isBulletPoints ? "bullet points" : "paragraph"
                                } section`}
                            />
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
                {/* Body (collapsible) */}
                {!collapsedSections.subjective && (
                  <div className="px-4 pb-6 sm:px-6">
                    {soapSectionLoading.subjective ||
                      hpiRegenerating ||
                      templateRegenerating ? (
                      <SoapSectionSkeleton />
                    ) : (
                      <div className="space-y-3">
                        {splitSectionIntoBlocks(
                          "subjective",
                          sectionContent.subjective || "",
                        ).map((item, i) => (
                          <InlineEditableLine
                            key={i}
                            text={item}
                            isParagraph
                            formatAsParagraphs={(content) =>
                              formatAsParagraphs(content, "subjective")
                            }
                            onFeedback={(section, type, content, contentType) =>
                              !isReadOnly &&
                              setFeedbackModal({
                                isOpen: true,
                                section,
                                type,
                                content,
                                contentType,
                              })
                            }
                            onSave={(section, newText) =>
                              !isReadOnly && saveInlineEdit(section, newText, i)
                            }
                            section="subjective"
                            feedbackSubmitted={getParagraphFeedbackState(item)}
                            soapNoteId={soapNote.soapNoteId}
                            onUpdateSoapNote={onUpdateSoapNote}
                            paragraphIndex={i}
                            readOnly={isReadOnly}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ========== OBJECTIVE ========== */}
              <div className="border-b border-gray-100">
                <div
                  className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-4 sm:p-6 cursor-pointer select-none sticky top-0 bg-white z-10"
                  onClick={() =>
                    setCollapsedSections((prev) => ({
                      ...prev,
                      objective: !prev.objective,
                    }))
                  }
                >
                  <div className="flex items-center gap-2">
                    {collapsedSections.objective ? (
                      <ChevronRight size={18} className="text-gray-600" />
                    ) : (
                      <ChevronDown size={18} className="text-gray-600" />
                    )}
                    <h3 className="text-xl font-semibold text-gray-900">
                      Objective
                    </h3>
                  </div>
                  <div
                    className="flex items-center gap-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <VersionIndicator
                      section="objective"
                      currentVersion={getCurrentSectionVersion("objective")}
                      hasBeenEdited={hasSectionBeenEdited("objective")}
                      onShowHistory={handleShowFeedbackHistory}
                    />
                    {(() => {
                      const content = sectionContent.objective || "";
                      const bullets = splitBullets(content);
                      const isBulletPoints = bullets.length > 1;
                      const currentFeedback =
                        getSectionFeedbackState("objective");
                      return (
                        <>
                          {/* Thumbs Up Button */}
                          <button
                            onClick={() =>
                              !isReadOnly &&
                              setFeedbackModal({
                                isOpen: true,
                                section: "objective",
                                type: "like",
                                content: "",
                                contentType: "entire-section",
                              })
                            }
                            disabled={currentFeedback !== null || isReadOnly}
                            className={`p-2 rounded-full transition ${currentFeedback === "like"
                                ? "bg-green-100 cursor-default"
                                : currentFeedback !== null
                                  ? "bg-gray-100 cursor-not-allowed"
                                  : "hover:bg-green-100"
                              }`}
                          >
                            {currentFeedback === "like" ? (
                              <ThumbsUp
                                fill="#17803C"
                                className="w-5 h-5 text-green-600"
                              />
                            ) : currentFeedback !== null || isReadOnly ? (
                              <ThumbsUp className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ThumbsUp className="w-5 h-5 text-gray-600 hover:text-green-600" />
                            )}
                          </button>

                          {/* Thumbs Down Button */}
                          <button
                            onClick={() =>
                              !isReadOnly &&
                              setFeedbackModal({
                                isOpen: true,
                                section: "objective",
                                type: "dislike",
                                content: "",
                                contentType: "entire-section",
                              })
                            }
                            disabled={currentFeedback !== null || isReadOnly}
                            className={`p-2 rounded-full transition ${currentFeedback === "dislike"
                                ? "bg-red-100 cursor-default"
                                : currentFeedback !== null
                                  ? "bg-gray-100 cursor-not-allowed"
                                  : "hover:bg-red-100"
                              }`}
                          >
                            {currentFeedback === "dislike" ? (
                              <ThumbsDown
                                fill="#dc2626"
                                className="w-5 h-5 text-red-600"
                              />
                            ) : currentFeedback !== null || isReadOnly ? (
                              <ThumbsDown className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ThumbsDown className="w-5 h-5 text-gray-600 hover:text-red-600" />
                            )}
                          </button>
                          {copiedSection === "objective" ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : (
                            <Copy
                              className="w-6 h-6 cursor-pointer text-gray-600 hover:text-gray-800"
                              onClick={() =>
                                handleCopyClick("objective", content)
                              }
                            />
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
                {!collapsedSections.objective && (
                  <div className="px-4 pb-6 sm:px-6">
                    {soapSectionLoading.objective || templateRegenerating ? (
                      <SoapSectionSkeleton />
                    ) : (
                      <div className="space-y-3">
                        {splitSectionIntoBlocks(
                          "objective",
                          sectionContent.objective || "",
                        ).map((item, i) => (
                          <InlineEditableLine
                            key={i}
                            text={item}
                            isParagraph
                            formatAsParagraphs={(content) =>
                              formatAsParagraphs(content, "objective")
                            }
                            onFeedback={(section, type, content, contentType) =>
                              !isReadOnly &&
                              setFeedbackModal({
                                isOpen: true,
                                section,
                                type,
                                content,
                                contentType,
                              })
                            }
                            onSave={(section, newText) =>
                              !isReadOnly && saveInlineEdit(section, newText, i)
                            }
                            section="objective"
                            feedbackSubmitted={getParagraphFeedbackState(item)}
                            soapNoteId={soapNote.soapNoteId}
                            onUpdateSoapNote={onUpdateSoapNote}
                            paragraphIndex={i}
                            readOnly={isReadOnly}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ========== ASSESSMENT & PLAN ========== */}
              <div data-soap-section="assessmentAndPlan">
                <div
                  className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-4 sm:p-6 cursor-pointer select-none sticky top-0 bg-white z-10"
                  onClick={() =>
                    setCollapsedSections((prev) => ({
                      ...prev,
                      assessmentAndPlan: !prev.assessmentAndPlan,
                    }))
                  }
                >
                  <div className="flex items-center gap-2">
                    {collapsedSections.assessmentAndPlan ? (
                      <ChevronRight size={18} className="text-gray-600" />
                    ) : (
                      <ChevronDown size={18} className="text-gray-600" />
                    )}
                    <h3 className="text-xl font-semibold text-gray-900">
                      Assessment & Plan
                    </h3>
                  </div>
                  <div
                    className="flex items-center gap-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <VersionIndicator
                      section="assessmentAndPlan"
                      currentVersion={getCurrentSectionVersion(
                        "assessmentAndPlan",
                      )}
                      hasBeenEdited={hasSectionBeenEdited("assessmentAndPlan")}
                      onShowHistory={handleShowFeedbackHistory}
                    />
                    {(() => {
                      const assessmentContent = collapseRedundantPlanHeadings(
                        sectionContent.assessment || "",
                      );
                      const planContent = stripStandalonePlanHeading(
                        sectionContent.plan || "",
                      );
                      const combinedContent = `Assessment:\n${normalizeCopyText(assessmentContent)}\n\nPlan:\n${normalizeCopyText(planContent)}`;
                      const currentFeedback =
                        getSectionFeedbackState("assessmentAndPlan");
                      return (
                        <>
                          <button
                            onClick={() =>
                              !isReadOnly &&
                              setFeedbackModal({
                                isOpen: true,
                                section: "assessmentAndPlan",
                                type: "like",
                                content: "",
                                contentType: "entire-section",
                              })
                            }
                            disabled={currentFeedback !== null || isReadOnly}
                            className={`p-2 rounded-full transition ${currentFeedback === "like"
                                ? "bg-green-100 cursor-default"
                                : currentFeedback !== null
                                  ? "bg-gray-100 cursor-not-allowed"
                                  : "hover:bg-green-100"
                              }`}
                          >
                            {currentFeedback === "like" ? (
                              <ThumbsUp
                                fill="#17803C"
                                className="w-5 h-5 text-green-600"
                              />
                            ) : currentFeedback !== null || isReadOnly ? (
                              <ThumbsUp className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ThumbsUp className="w-5 h-5 text-gray-600 hover:text-green-600" />
                            )}
                          </button>
                          <button
                            onClick={() =>
                              !isReadOnly &&
                              setFeedbackModal({
                                isOpen: true,
                                section: "assessmentAndPlan",
                                type: "dislike",
                                content: "",
                                contentType: "entire-section",
                              })
                            }
                            disabled={currentFeedback !== null || isReadOnly}
                            className={`p-2 rounded-full transition ${currentFeedback === "dislike"
                                ? "bg-red-100 cursor-default"
                                : currentFeedback !== null
                                  ? "bg-gray-100 cursor-not-allowed"
                                  : "hover:bg-red-100"
                              }`}
                          >
                            {currentFeedback === "dislike" ? (
                              <ThumbsDown
                                fill="#dc2626"
                                className="w-5 h-5 text-red-600"
                              />
                            ) : currentFeedback !== null || isReadOnly ? (
                              <ThumbsDown className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ThumbsDown className="w-5 h-5 text-gray-600 hover:text-red-600" />
                            )}
                          </button>
                          {copiedSection === "assessmentPlan" ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : (
                            <Copy
                              className="w-6 h-6 cursor-pointer text-gray-600 hover:text-gray-800"
                              onClick={() =>
                                handleCopyClick(
                                  "assessmentPlan",
                                  combinedContent,
                                )
                              }
                            />
                          )}
                          <Mail
                            className="w-5 h-5 cursor-pointer text-blue-600 hover:text-blue-800"
                            onClick={() => setShowEmailShare(true)}
                            title="Share via Email"
                          />
                        </>
                      );
                    })()}
                  </div>
                </div>
                {!collapsedSections.assessmentAndPlan && (
                  <div
                    className="px-6 pb-6"
                    ref={(el) => {
                      sectionBodyRefs.current.assessmentAndPlan = el;
                    }}
                    // Bubble up from any icd-heading-clickable click
                    onClick={(e) => {
                      const heading = e.target.closest("[data-icd-code]");
                      if (heading) {
                        const code = heading.getAttribute("data-icd-code");
                        if (code) openIcdCodeEditModal(code);
                      }
                    }}
                  >
                    {soapSectionLoading.assessment ||
                      soapSectionLoading.plan ||
                      templateRegenerating ? (
                      <div className="space-y-4">
                        <SoapSectionSkeleton />
                        <SoapSectionSkeleton />
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <Icd10CodeChips
                          codes={[
                            ...new Set([
                              ...extractIcdCodesFromText(
                                sectionContent.assessment || "",
                              ),
                              ...extractIcdCodesFromText(
                                sectionContent.plan || "",
                              ),
                            ]),
                          ]}
                          onCodeClick={openIcdCodeEditModal}
                          readOnly={isReadOnly}
                          trailingAction={
                            !isReadOnly ? (
                              <Icd10AddCodeButton
                                onClick={() => setIcdAddModalOpen(true)}
                              />
                            ) : null
                          }
                        />

                        {sectionContent.assessment && (
                          <div className="space-y-3">
                            {splitSectionIntoBlocks(
                              "assessment",
                              sectionContent.assessment,
                            ).map((item, i) => (
                              <InlineEditableLine
                                key={`assessment-${i}`}
                                text={item}
                                isParagraph
                                formatAsParagraphs={(content) =>
                                  formatAsParagraphs(content, "assessment")
                                }
                                onFeedback={(
                                  section,
                                  type,
                                  feedbackContent,
                                  contentType,
                                ) =>
                                  !isReadOnly &&
                                  setFeedbackModal({
                                    isOpen: true,
                                    section,
                                    type,
                                    content: feedbackContent,
                                    contentType,
                                  })
                                }
                                onSave={(section, newText) =>
                                  !isReadOnly &&
                                  saveInlineEdit(section, newText, i)
                                }
                                section="assessment"
                                feedbackSubmitted={getParagraphFeedbackState(
                                  item,
                                )}
                                soapNoteId={soapNote.soapNoteId}
                                onUpdateSoapNote={onUpdateSoapNote}
                                paragraphIndex={i}
                                readOnly={isReadOnly}
                              />
                            ))}
                          </div>
                        )}

                        {(() => {
                          const { clinicalPlan, followUp } =
                            splitPlanFromFollowUp(sectionContent.plan || "");
                          return (
                            <>
                              {clinicalPlan && (
                                <div className="space-y-3">
                                  {splitSectionIntoBlocks(
                                    "plan",
                                    clinicalPlan,
                                  ).map((item, i) => (
                                    <InlineEditableLine
                                      key={`plan-clinical-${i}`}
                                      text={item}
                                      isParagraph
                                      formatAsParagraphs={formatAsParagraphs}
                                      onFeedback={(
                                        section,
                                        type,
                                        feedbackContent,
                                        contentType,
                                      ) =>
                                        !isReadOnly &&
                                        setFeedbackModal({
                                          isOpen: true,
                                          section,
                                          type,
                                          content: feedbackContent,
                                          contentType,
                                        })
                                      }
                                      onSave={(_section, newText) =>
                                        !isReadOnly &&
                                        savePlanMergedBlock(
                                          "clinical",
                                          i,
                                          newText,
                                        )
                                      }
                                      section="plan"
                                      feedbackSubmitted={getParagraphFeedbackState(
                                        item,
                                      )}
                                      soapNoteId={soapNote.soapNoteId}
                                      onUpdateSoapNote={onUpdateSoapNote}
                                      paragraphIndex={i}
                                      readOnly={isReadOnly}
                                    />
                                  ))}
                                </div>
                              )}
                              {followUp && (
                                <div className="space-y-3">
                                  {splitSectionIntoBlocks("plan", followUp).map(
                                    (item, i) => (
                                      <InlineEditableLine
                                        key={`plan-followup-${i}`}
                                        text={item}
                                        isParagraph
                                        formatAsParagraphs={formatAsParagraphs}
                                        onFeedback={(
                                          section,
                                          type,
                                          feedbackContent,
                                          contentType,
                                        ) =>
                                          !isReadOnly &&
                                          setFeedbackModal({
                                            isOpen: true,
                                            section,
                                            type,
                                            content: feedbackContent,
                                            contentType,
                                          })
                                        }
                                        onSave={(_section, newText) =>
                                          !isReadOnly &&
                                          savePlanMergedBlock(
                                            "followUp",
                                            i,
                                            newText,
                                          )
                                        }
                                        section="plan"
                                        feedbackSubmitted={getParagraphFeedbackState(
                                          item,
                                        )}
                                        soapNoteId={soapNote.soapNoteId}
                                        onUpdateSoapNote={onUpdateSoapNote}
                                        paragraphIndex={i}
                                        readOnly={isReadOnly}
                                      />
                                    ),
                                  )}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            {soapNote?.soapNoteId && (problemList.length > 0 || isLoadingProblemList) && (
              <div ref={problemListRef}>
                {(() => {
                  const orderedProblems = sortProblemsBySoapOrder(
                    problemList,
                    sectionContent.assessment || "",
                    sectionContent.plan || "",
                  );
                  const isHidden = (p) =>
                    hiddenIcdCodes.includes(String(p.code).toUpperCase());
                  return (
                    <SoapIcd10ProblemListPanel
                      problemList={orderedProblems.filter((p) => !isHidden(p))}
                      hiddenProblems={orderedProblems.filter(isHidden)}
                      onHideCode={handleHideIcd10Code}
                      onRestoreCode={handleRestoreIcd10Code}
                      isReadOnly={isReadOnly}
                      isLoading={isLoadingProblemList}
                      onOpenAddModal={() => setIcdAddModalOpen(true)}
                      onCodeEdit={openIcdCodeEditModal}
                    />
                  );
                })()}
              </div>
            )}

            <Icd10AddCodeModal
              open={icdAddModalOpen}
              onOpenChange={setIcdAddModalOpen}
              existingCodes={problemList
                .map((p) => p.code)
                .filter(
                  (c) => !hiddenIcdCodes.includes(String(c).toUpperCase()),
                )}
              isReadOnly={isReadOnly}
              onAdd={handleAddIcd10Code}
            />

            <Icd10CodeChangeModal
              open={icdEditModal.open}
              onOpenChange={(open) =>
                setIcdEditModal((prev) => ({ ...prev, open }))
              }
              currentCode={icdEditModal.code}
              currentDescription={icdEditModal.description}
              assessmentText={sectionContent.assessment || ""}
              isReadOnly={isReadOnly}
              onReplace={handleReplaceIcd10Code}
            />

            {/* Future: practice-partner highlight / trusted|external badges
            <PracticePartnerDetailModal
              open={partnerDetailModal.open}
              onOpenChange={(open) =>
                setPartnerDetailModal((prev) => ({ ...prev, open }))
              }
              partner={partnerDetailModal.partner}
              mentionName={partnerDetailModal.mentionName}
              kind={partnerDetailModal.kind}
            />
            */}

            {/* Feedback Modal */}

            <FeedbackModal
              isOpen={feedbackModal.isOpen}
              section={feedbackModal.section}
              type={feedbackModal.type}
              content={feedbackModal.content}
              contentType={feedbackModal.contentType}
              setFeedbackModal={setFeedbackModal}
              soapNoteId={soapNote.soapNoteId}
              setFeedbackSubmitted={setFeedbackSubmitted}
              onFeedbackSubmitted={updateFeedbackState}
            />
          </div>
        )}

        {activeSection === "diagnostics" && (
          <div className="space-y-6">
            {/* Diagnoses */}
            {advancedData.diagnostics.totalDiagnoses > 0 && (
              <SectionCard
                title="Clinical Diagnoses"
                icon={ClipboardList}
                badge={`${advancedData.diagnostics.totalDiagnoses} Total`}
              >
                <div className="space-y-4">
                  {advancedData.diagnostics.primaryDiagnoses.map(
                    (diagnosis, index) => (
                      <DiagnosisCard
                        key={index}
                        diagnosis={diagnosis}
                        isPrimary={true}
                      />
                    ),
                  )}
                  {advancedData.diagnostics.secondaryDiagnoses.map(
                    (diagnosis, index) => (
                      <DiagnosisCard
                        key={index}
                        diagnosis={diagnosis}
                        isPrimary={false}
                      />
                    ),
                  )}
                </div>
              </SectionCard>
            )}

            {/* Medications & Procedures */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <SectionCard
                title="Medications"
                icon={Pill}
                isEmpty={advancedData.medications.length === 0}
              >
                {advancedData.medications.length > 0 && (
                  <div className="space-y-3">
                    {advancedData.medications.map((med, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-3 p-3 bg-purple-50 border border-purple-200 rounded-lg"
                      >
                        <Pill className="w-5 h-5 text-purple-600 flex-shrink-0" />
                        <span className="text-purple-800 font-medium">
                          {med}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>

              <SectionCard
                title="Procedures & Tests"
                icon={Activity}
                isEmpty={advancedData.procedures.length === 0}
              >
                {advancedData.procedures.length > 0 && (
                  <div className="space-y-3">
                    {advancedData.procedures.map((proc, index) => (
                      <div
                        key={index}
                        className="flex items-start space-x-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg"
                      >
                        <Activity className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                        <span className="text-indigo-800">{proc}</span>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>
            </div>

            {/* Follow-up Information */}
            {(advancedData.followUp.timeframe ||
              advancedData.followUp.instructions ||
              advancedData.followUp.returnConditions.length > 0) && (
                <SectionCard title="Follow-up Care" icon={Calendar}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {advancedData.followUp.timeframe && (
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <h4 className="font-semibold text-blue-800 mb-2">
                          Next Appointment
                        </h4>
                        <p className="text-blue-700">
                          {advancedData.followUp.timeframe}
                        </p>
                      </div>
                    )}
                    {advancedData.followUp.instructions && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <h4 className="font-semibold text-green-800 mb-2">
                          Instructions
                        </h4>
                        <p className="text-green-700">
                          {advancedData.followUp.instructions}
                        </p>
                      </div>
                    )}
                  </div>
                  {advancedData.followUp.returnConditions.length > 0 && (
                    <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <h4 className="font-semibold text-yellow-800 mb-2">
                        Return If:
                      </h4>
                      <ul className="text-yellow-700 space-y-1">
                        {advancedData.followUp.returnConditions.map(
                          (condition, index) => (
                            <li
                              key={index}
                              className="flex items-start space-x-2"
                            >
                              <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                              <span>{condition}</span>
                            </li>
                          ),
                        )}
                      </ul>
                    </div>
                  )}
                </SectionCard>
              )}
          </div>
        )}

        {transcriptionPanel && (
          <div className="md:mr-8">{transcriptionPanel}</div>
        )}
        {/* Raw Content Section — hidden from UI by default (see SHOW_COMPLETE_RAW_DOCUMENTATION) */}
        {SHOW_COMPLETE_RAW_DOCUMENTATION && (
          <details className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <summary className="px-6 py-4 cursor-pointer hover:bg-gray-50 border-b border-gray-100 flex items-center justify-between transition-colors">
              <div className="flex items-center space-x-3">
                <FileText className="w-6 h-6 text-gray-600" />
                <span className="text-lg font-semibold text-gray-900">
                  Complete Raw Documentation
                </span>
              </div>
              <div className="text-gray-400">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </summary>
            <div className="px-6 py-6">
              <div className="bg-gray-50 rounded-xl p-6 max-h-96 overflow-auto">
                <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono leading-relaxed">
                  {soapNote.rawContent || JSON.stringify(soapNote, null, 2)}
                </pre>
              </div>
            </div>
          </details>
        )}
      </div>

      {!isReadOnly && activeSection === "soap" && (
        <MagicEditorButton
          disabled={isAiFeatureLocked || isProPlusLocked}
          lockMessage={
            isProPlusLocked
              ? "Magic Editor is available on Professional and Elite. Upgrade to unlock."
              : getAccountLimitMessage()
          }
          onClick={() => {
            if (isAiFeatureLocked || isProPlusLocked) {
              toast.error(
                isProPlusLocked
                  ? "Magic Editor is available on Professional and Elite. Upgrade to unlock."
                  : getAccountLimitMessage()
              );
              return;
            }
            setShowMagicEditor(true);
          }}
        />
      )}

      {!isReadOnly && (
        <NotepadEditorButton onClick={() => setShowNotepadEditor(true)} />
      )}
      <MagicEditorWithWebSocket
        isOpen={showMagicEditor && !isAiFeatureLocked && !isProPlusLocked}
        onClose={() => setShowMagicEditor(false)}
        soapNote={soapNote}
        userId={currentUser?.userId}
        token={localStorage.getItem("token") || "test-token"}
        onStartProcessing={() => {
          setSoapSectionLoading({
            subjective: true,
            objective: true,
            assessment: true,
            plan: true,
          });
        }}
        onUpdateSoapNote={async (updatedSoapNote) => {
          // Preserve the pronoun if the editor didn't return one
          if (
            soapNote?.metadata?.pronouns &&
            !updatedSoapNote.metadata?.pronouns
          ) {
            if (!updatedSoapNote.metadata) updatedSoapNote.metadata = {};
            updatedSoapNote.metadata.pronouns = soapNote.metadata.pronouns;
          }
          setSoapNote(updatedSoapNote);
          setSoapSectionLoading({
            subjective: false,
            objective: false,
            assessment: false,
            plan: false,
          });

          try {
            const sections = ["subjective", "objective", "assessment", "plan"];

            for (const section of sections) {
              const oldContent = soapNote[section] || "";
              const newContent = updatedSoapNote[section] || "";
              const versionContent = normalizeVersionContent(newContent);
              const oldComparableContent = normalizeContentForVersionComparison(oldContent);
              const newComparableContent = normalizeContentForVersionComparison(versionContent);

              if (
                oldComparableContent !== newComparableContent &&
                newComparableContent !== ""
              ) {
                await humanFeedbackService.incrementSectionVersion(
                  updatedSoapNote.soapNoteId,
                  section,
                  versionContent,
                  "ai_correction",
                );
                console.log(
                  `Section version incremented for ${section} (Magic Editor)`,
                );
              }
            }
            if (loadFeedbackState) {
              await loadFeedbackState();
            }
          } catch (versionError) {
            console.error(
              "Failed to increment section versions for Magic Editor:",
              versionError,
            );
          }
        }}
        aiService={aiService}
      />

      {/* Notepad Editor Modal */}
      <NotepadEditorModal
        isOpen={showNotepadEditor}
        onClose={() => setShowNotepadEditor(false)}
        soapNote={parsedSections}
      />

      {/* Email Share Modal */}
      <EmailShareModal
        isOpen={showEmailShare}
        onClose={() => setShowEmailShare(false)}
        soapNote={parsedSections}
      />

      <FeedbackHistoryModal
        isOpen={showFeedbackHistory}
        onClose={handleCloseFeedbackHistory}
        soapNoteId={soapNote?.soapNoteId}
        section={selectedSectionForHistory}
        currentVersion={
          selectedSectionForHistory
            ? getCurrentSectionVersion(selectedSectionForHistory)
            : 1
        }
        onVersionRestored={handleVersionRestored}
      />
      {/* Admin Action Modal */}
      {/* <AdminActionModal
        isOpen={showAdminActionModal.isOpen}
        onClose={() => setShowAdminActionModal({ isOpen: false, action: null })}
        onSubmit={handleAdminAction}
        actionType={showAdminActionModal.action}
        loading={actionLoading}
      /> */}

      {/* Submit Review Modal */}
      {/* <SubmitReviewModal
        isOpen={showSubmitReviewModal}
        onClose={() => setShowSubmitReviewModal(false)}
        onSubmit={handleSubmitForReview}
        loading={actionLoading}
      /> */}
      {/* {showAssignModal && (
        <AssignScribeModal
          session={convertSoapNoteToSession(soapNote)}
          assignableUsers={assignableUsers}
          sessions={allSessions || []} // Optional: pass sessions if you want workload calculation
          onClose={() => setShowAssignModal(false)}
          onAssign={handleAssignSession}
          onReassign={handleAssignSession}
          onRequestExtension={(sessionId, days, reason) => {
            // Optional: handle extension requests
            // console.log("Extension requested:", sessionId, days, reason);
          }}
        />
      )} */}
    </div>
  );
}
